import isEqual from "lodash/isEqual";

import { THREE, CSS3DObject, CSS3DRenderer, OrbitControls, SVGLoader, threeToColor } from "@scrolly/renderer";
import crawl from "../utils/crawl";
import { colorToHex, Easings, degreeToRad, interpolateLinearDirectly } from "@scrolly/renderer";

import RendererStore from "./RendererStore";
import { WebGLAssets } from "./webglAssets";
import { applyDataAnim } from "./dataAnim";

import type {
    AssetContent,
    ImageContent,
    LightContent,
    LottieContent,
    MapContent,
    MeshOptions,
    MeshOptionsBump,
    MeshPhongOptions,
    MeshStandardOptions,
    Object3DContent,
    PointLightContent,
    SpotLightContent,
    SvgContent,
    TextContent,
    VideoContent,
} from "@scrolly/post-types";
import { interpolateWaypoints, getActiveMarkers } from "./MapInterpolation";
import { hasMeshBumpOptions, hasMeshOptions, MaterialType } from "@scrolly/renderer";

import { LightType } from "./LightType";
import { LayerType } from "./LayerType";
import Video from "./Video";
import LottiePlayer from "./Lottie";
import { DEFAULT_FONT, FONTS } from "./assets/fonts";
import assets from "./assets";
import type { PresentationRender } from "./PresentationRender";
import type { LayerRender } from "./LayerRender";
import type SlidePlayer from "./SlidePlayer";
import { logDebug, logError, logWarning } from "../utils/logger";
import type { CustomFonts, ImageAssetBackend, PostJson } from "@scrolly/post-types";
import { Text, preloadFont } from "troika-three-text";
import { AssetLibraryCategory, AssetLibraryType } from "./assets/AssetLibrary";

import {
    fontItalianno,
    fontJetBrains,
    fontMerriweather,
    fontMerriweatherSans,
    fontNotoSans,
    fontNotoSerif,
    fontPortLligatSlab,
    fontRoboto,
    fontSourceSans3,
    fontSourceSerif4,
} from "../assets/fonts";
import type { ImageOptimizer } from "../utils/imageLoader";

const MIN_VALUE = 1 / Number.MAX_VALUE;
const MIN_SCALE: Vector3Tuple = [MIN_VALUE, MIN_VALUE, MIN_VALUE];
const CLEAR_COLOR = 0x000000;

export interface RendererProps {
    start: (animate: (timer: number) => void) => void;
    stop: () => void;
    renderer: RendererStore;
    slidePlayer: SlidePlayer;
    isProduction?: boolean;
    // editorStore?: EditorStore;
    presentation: PresentationRender;
    /** When true, Sentence-Bloom HTML SVG stays hidden (cover / beforeStory zone). */
    isCoverActive?: () => boolean;
}

function getLayerContentUrl(assetContent: AssetContent) {
    return assetContent.ppiUrl ? assetContent.ppiUrl : assetContent.url;
}

export default class Renderer {
    width = 0;
    height = 0;
    camera: THREE.PerspectiveCamera | undefined;
    webglRendererDom: HTMLDivElement;
    cssRendererDom: HTMLDivElement;
    webGLRenderer: THREE.WebGLRenderer | undefined;
    css3DRenderer: CSS3DRenderer;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    templateJson: any;

    setImagesOnStart: boolean = false;
    setVideosOnStart: boolean = false;
    setGpuTextOnStart: boolean = false;
    setVideoJit: boolean = false;

    visibilityOverrides: Record<string, boolean> = {};
    groups: { [id: string]: THREE.Group } = {};
    groupsCSS: { [id: string]: CSS3DObject } = {};
    layerMeshes: {
        [id: string]: THREE.Mesh | THREE.SpotLight | THREE.DirectionalLight | THREE.PointLight;
    } = {};
    slideLayerMeshes: { [id: string]: THREE.Mesh } = {};
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    layerContentCache: { [id: string]: any } = {};
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    objectCache: { [id: string]: any } = {};
    layer3DCache: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [id: string]: any;
    } = {};

    webGlScene: THREE.Scene | undefined;
    css3DScene: THREE.Scene | undefined;
    groupRoot: THREE.Group | undefined;
    groupRootCSS: CSS3DObject | undefined;
    uiRoot: THREE.Group | undefined;
    ambientLight: THREE.AmbientLight | undefined;

    controls: OrbitControls | undefined;

    videoCache: {
        [key: string]: Video;
    } = {};

    lottieCache: {
        [key: string]: LottiePlayer;
    } = {};

    layerState3d: {
        [key: string]: THREE.Group;
    } = {};

    layerStateCSS3d: {
        [key: string]: CSS3DObject;
    } = {};

    lightState: {
        [key: string]: THREE.PointLight | THREE.DirectionalLight;
    } = {};

    lightHelpers: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: any;
    } = {};

    lightShadowHelpers: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: any;
    } = {};

    layerIsLoading: {
        [key: string]: boolean;
    } = {};

    layerScrollBindingsFired: Record<string, Set<string>> = {};

    svgOverlays: { [key: string]: HTMLDivElement } = {};

    // Interactive map layer state (MapTiler SDK / MapLibre GL)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mapInstances: { [key: string]: any } = {};
    mapContainers: { [key: string]: HTMLDivElement } = {};
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private mapSdk: any = null; // cached @maptiler/sdk module

    pivots: {
        [key: string]: THREE.Group;
    } = {};

    pivotsCSS: {
        [key: string]: CSS3DObject;
    } = {};

    pivotsCircles: {
        [key: string]: THREE.Mesh;
    } = {};

    safeSquare: THREE.Mesh | undefined;

    props: RendererProps;

    animationTimeOut: ReturnType<typeof setTimeout> | undefined;
    interval: ReturnType<typeof setTimeout> | undefined;
    imageOptimizer: ImageOptimizer;
    _visibilityHandler: (() => void) | undefined;

    constructor(
        webglRendererDom: HTMLDivElement,
        cssRendererDom: HTMLDivElement,
        props: RendererProps,
        imageOptimizer: ImageOptimizer
    ) {
        this.webglRendererDom = webglRendererDom;
        this.cssRendererDom = cssRendererDom;
        this.animate = this.animate.bind(this);
        this.props = {
            renderer: props.renderer,
            slidePlayer: props.slidePlayer,
            // editorStore: props.editorStore,
            start: props.start,
            stop: props.stop,
            isProduction: props.isProduction,
            presentation: props.presentation,
            isCoverActive: props.isCoverActive,
        };
        this.css3DRenderer = new CSS3DRenderer();
        this.imageOptimizer = imageOptimizer;
    }

    setProps(props: RendererProps) {
        this.props = {
            renderer: props.renderer,
            slidePlayer: props.slidePlayer,
            // editorStore: props.editorStore,
            start: props.start,
            stop: props.stop,
            isProduction: props.isProduction,
            presentation: props.presentation,
            isCoverActive: props.isCoverActive,
        };
    }

    uploadToGpu(object3D: THREE.Group) {
        if (!this.webGLRenderer || !this.camera || !this.webGlScene) {
            throw new Error("Called RendererWebGL#uploadToGpu before calling init function.");
        }
        const { visible } = object3D;
        const originalPosition = object3D.position.clone();
        object3D.position.set(0, 0, 0);
        object3D.visible = true;
        this.webGlScene.add(object3D);

        const culling = object3D.frustumCulled;
        object3D.frustumCulled = false;
        //  render
        this.webGLRenderer.render(this.webGlScene, this.camera);
        object3D.position.copy(originalPosition);
        object3D.visible = visible;

        //  pop
        this.webGlScene.remove(object3D);
        object3D.frustumCulled = culling;
    }

    init() {
        const rendererStore = this.props.renderer;

        if (!this.webglRendererDom) {
            return;
        }

        /*
        const message = 'ready';
        parent.postMessage(message, 'https://de-scrolly-frontend-test.ppi.media/');
        parent.postMessage(message, 'https://de-scrolly-frontend-dev.ppi.media/');

        window.addEventListener('message', (e) => {
      console.log("incoming message");
      console.log(e);
      if( e.data.messageId === "selectedSlide" ) {

          WebGLAssets.selectedIndex = e.data.selectedSlideIndex;
          console.log( "selectedIndex: " + WebGLAssets.selectedIndex);

      }

      if( e.data.messageId === "json" ) {

          console.log( "currentimageobject: ");
          WebGLAssets.currentImageObject = e.data.json.data.data.layers[WebGLAssets.selectedIndex];
          console.log( WebGLAssets.currentImageObject )

      }
        });

        document.addEventListener('pointerdown', (e) => {
      console.log("touchstart");
        });

        document.addEventListener('pointerup', (e) => {
      console.log("touchend");
        });

        document.body.addEventListener('pointerdown', (e) => {
      console.log("pointer down");
        });

        document.body.addEventListener('pointerup', (e) => {
      console.log("pointer up");
        });

        this.webglRendererDom.addEventListener('pointerup', (e) => {
      console.log("renderdom pointer up");
        });
        */

        this._visibilityHandler = () => {
            if (document.hidden) {
                for (const video of Object.values(this.videoCache)) {
                    video.pause();
                    video.seek(0);
                }
                for (const lottiePlayer of Object.values(this.lottieCache)) {
                    lottiePlayer.pause();
                }
            }
        };
        document.addEventListener("visibilitychange", this._visibilityHandler);

        const width = this.webglRendererDom.clientWidth;
        const height = this.webglRendererDom.clientHeight;

        const FOV = rendererStore.fov;
        const FAR = rendererStore.far;
        const { cameraLookAt } = rendererStore;
        const { cameraPosition } = rendererStore;

        // I made some experiments with the Orthographic Camera, but they were not satisfying, so
        // a camera with a low FOV seems to be the way to go
        this.camera = new THREE.PerspectiveCamera(FOV, width / height, 1, FAR);

        this.camera.position.copy(new THREE.Vector3(...cameraPosition!));
        this.camera.lookAt(new THREE.Vector3(...cameraLookAt!));
        this.camera.updateProjectionMatrix();

        // This should later be extended for debugging, gives the ability to easily navigate the camera
        this.controls = new OrbitControls(this.camera, this.webglRendererDom);
        this.controls.enabled = rendererStore.useOrbitControls;

        // WEBGL Renderer
        this.webGLRenderer = new THREE.WebGLRenderer({
            // it is important that we set alpha equals true, so the mixed mode works
            alpha: true,
            antialias: this.props.renderer.antialiasing,
            precision: "lowp",
            // premultipliedAlpha: true,
        });

        this.webGLRenderer.shadowMap.enabled = true;
        this.webGLRenderer.shadowMap.type = THREE.PCFSoftShadowMap;

        this.webGLRenderer.setSize(width, height);
        this.css3DRenderer.setSize(width, height);

        // Override inline pixel styles with percentage-based sizing so the canvas
        // fills its container when the viewport mode changes. The buffer dimensions
        // are updated separately via setSize(w, h, false) in animate().
        this.webGLRenderer.domElement.style.width = '100%';
        this.webGLRenderer.domElement.style.height = '100%';

        // Transparent clear so the wrapper element's background (set by VisualStoryPlayer
        // from slide_options.background_color) shows through. Without this, the scene
        // shows a hardcoded black behind any SVG/DOM overlays smaller than the viewport.
        this.webGLRenderer.setClearColor(CLEAR_COLOR, 0);

        // Accessibility: mark the WebGL canvas for screen readers
        this.webGLRenderer.domElement.setAttribute('role', 'img');
        this.webGLRenderer.domElement.setAttribute('aria-label', 'Scrolly Animation');

        // append doms
        this.webglRendererDom.appendChild(this.webGLRenderer.domElement);
        //this.cssRendererDom.appendChild(this.css3DRenderer.domElement);

        const webGLScene = new THREE.Scene();
        //const css3DScene = new THREE.Scene();
        this.webGlScene = webGLScene;
        //this.css3DScene = css3DScene;
        // window.THREE removed — was leaking the entire Three.js library as a global

        // The WebGL scene needs at least one light in order to be able to see the
        // layers. It does not necessarily have to be an ambient light though.
        const light = new THREE.AmbientLight(0xffffff, 0.2);
        this.ambientLight = light;
        this.webGlScene.add(light);
    }

    async updateMaterial(layerId: string, content: Object3DContent, updateDirectly = false) {
        const cachedLayerContent = this.layerContentCache[layerId];
        const contentChange = !cachedLayerContent || cachedLayerContent.material !== content.material;
        const isDirty = contentChange;
        const isLoading = this.layerIsLoading[layerId];
        const object = this.objectCache[layerId];

        if (((isDirty && !isLoading) || updateDirectly) && object) {
            if (!updateDirectly) {
                this.layerIsLoading[layerId] = true;
            }
            const loadingPromises: Array<Promise<void>> = [];

            // const qualityToUse = this.props.renderer.quality!;
            const resizeUrlIfPossible = (map: ImageAssetBackend) => {
                if (map.key) {
                    // TODO DESCR-218: revisit
                    // return createCdnImageUrl(map, qualityToUse.fraction * map.width);
                    return map.ppiUrl || map.url;
                } else {
                    // TODO Remove this once we make sure all stories have only assets with "key"
                    logWarning(`Did not found 'key' in layer '${layerId}', falling back to url: ${map.url}`);
                    return map.url;
                }
            };
            const object3DContent = content;
            const { type } = object3DContent.material;

            if (object3DContent.material.overwriteMaterial) {
                const getTextureAsset = (asset: ImageAssetBackend) => {
                    let result = assets.getAsset(asset.id);
                    if (!result) {
                        const resizedUrl = resizeUrlIfPossible(asset);
                        let resolve: () => void;
                        loadingPromises.push(
                            new Promise((r) => {
                                resolve = r;
                            })
                        );
                        result = assets.loadAsset(
                            {
                                id: asset.id,
                                content: resizedUrl,
                                category: AssetLibraryCategory.texture,
                                type: AssetLibraryType.texture,
                            },
                            () => {
                                resolve();
                            }
                        );
                    }
                    return result;
                };

                switch (type) {
                    // THREE JS Materials
                    case MaterialType.meshStandard: {
                        const material = new THREE.MeshStandardMaterial();
                        object.material = material;
                        const standardMeshOptions = object3DContent.material.options as MeshStandardOptions;
                        if (standardMeshOptions.metalnessMap) {
                            object.material.metalnessMap = getTextureAsset(standardMeshOptions.metalnessMap);
                        }
                        if (standardMeshOptions.roughnessMap) {
                            object.material.roughnessMap = getTextureAsset(standardMeshOptions.roughnessMap);
                        }
                        if (standardMeshOptions.metalness !== undefined) {
                            object.material.metalness = standardMeshOptions.metalness;
                        }
                        if (standardMeshOptions.refractionRatio !== undefined) {
                            object.material.refractionRatio = standardMeshOptions.refractionRatio;
                        }
                        break;
                    }
                    case MaterialType.meshBasic: {
                        const material = new THREE.MeshBasicMaterial();
                        object.material = material;
                        break;
                    }
                    case MaterialType.meshLambert: {
                        const material = new THREE.MeshLambertMaterial();
                        object.material = material;
                        break;
                    }
                    case MaterialType.meshPhong: {
                        const material = new THREE.MeshPhongMaterial();
                        object.material = material;
                        object.material.shininess =
                            (object3DContent.material.options as MeshPhongOptions).shininess || 0;
                        break;
                    }
                    default:
                }

                if (hasMeshOptions(content)) {
                    const options = object3DContent.material.options as MeshOptions;
                    object.material.wireframe = options.wireframe;
                    object.material.transparent = true;
                    object.material.color = options.color;
                    if (options.alphaMap) {
                        object.material.alphaMap = getTextureAsset(options.alphaMap);
                    }
                    if (options.map) {
                        object.material.map = getTextureAsset(options.map);
                    }
                    if (options.envMap) {
                        object.material.envMap = getTextureAsset(options.envMap);
                    }
                }
                if (hasMeshBumpOptions(content)) {
                    const options = object3DContent.material.options as MeshOptionsBump;
                    if (options.bumpMap) {
                        object.material.bumpMap = getTextureAsset(options.bumpMap);
                    }
                    if (options.displacementMap) {
                        object.material.displacementMap = getTextureAsset(options.displacementMap);
                    }
                    if (options.normalMap) {
                        object.material.normalMap = getTextureAsset(options.normalMap);
                    }
                    if (options.bumpScale !== undefined) {
                        object.material.bumpScale = options.bumpScale;
                    }
                    if (options.displacementScale !== undefined) {
                        object.material.displacementScale = options.displacementScale;
                    }
                    if (options.displacementBias !== undefined) {
                        object.material.displacementBias = options.displacementBias;
                    }
                }
            }

            object.receiveShadow = object3DContent.material.receiveShadow;
            object.castShadow = object3DContent.material.castShadow;

            this.layerContentCache[layerId] = content;

            // TODO Find a story that uses overwriteMaterial to test
            await Promise.allSettled(loadingPromises);
            if (!updateDirectly) {
                this.layerIsLoading[layerId] = false;
                this.props.renderer.loadedLayer(layerId, "material");
            }
        }
    }

    updateObject(layerId: string, content: Object3DContent, callback: (obj: THREE.Mesh) => void) {
        const animatable3DLayer = this.layerState3d[layerId];
        const cachedLayerContent = this.layerContentCache[layerId];
        // only reload when url changed
        const contentChange =
            // TODO Change to .key once we are sure all stories have only assets with .key
            !cachedLayerContent || content.url !== cachedLayerContent.url;
        const isDirty = !animatable3DLayer || contentChange;
        const isLoading = this.layerIsLoading[layerId];

        if (isDirty && !isLoading) {
            this.layerIsLoading[layerId] = true;

            // TODO DESCR-218: revisit
            // const url = createCdnAssetUrl(content);
            const url = content.url;

            const objLoaded = async (obj: TODOAny) => {
                // object does not have any children, abort
                if (!obj.children) {
                    logDebug("Object does not have any children");
                }

                let index = 0;
                this.objectCache[layerId] = obj;
                if (obj.material) {
                    obj.material.transparent = true;
                }
                obj.traverse((child: TODOAny) => {
                    const storyforgerId = `${layerId}-${index}`;
                    if (child.isMesh) {
                        child.castShadow = true;
                        child.receiveShadow = true;
                        child.material.transparent = true;
                    }
                    child.storyforgerId = storyforgerId;
                    this.objectCache[storyforgerId] = child;
                    index += 1;
                });

                callback(obj);
                this.uploadToGpu(obj);
                await this.updateMaterial(layerId, content, true);
                this.props.renderer.loadedLayer(layerId, url);

                this.layerState3d[layerId] = obj;
                this.layerIsLoading[layerId] = false;
                // cache content
                this.layerContentCache[layerId] = content;
                this.layerMeshes[layerId] = obj;
            };

            if (content.extension === "obj") {
                // Lazy-load OBJLoader chunk (M2 Plan-1.9-Phase-1-CP2): only fetched
                // when an object3d-Layer with .obj extension is instantiated.
                import('three/examples/jsm/loaders/OBJLoader.js').then(({ OBJLoader }) => {
                    const objLoader = new OBJLoader();
                    objLoader.load(url, objLoaded);
                }).catch((err) => {
                    logError(new Error(`Failed to load OBJLoader chunk: ${err}`));
                    this.layerIsLoading[layerId] = false;
                    this.props.renderer.loadedLayer(layerId, url);
                });
            }

            if (content.extension === "json") {
                // @ts-expect-error ts-migrate(2339) FIXME: Property 'JSONLoader' does not exist on type 'type... Remove this comment to see the full error message
                const jsonLoader = new THREE.JSONLoader();
                jsonLoader.load(url, (geometry: TODOAny, materials: TODOAny) => {
                    const object = new THREE.Mesh(geometry, materials);
                    objLoaded(object);
                });
            }

            if (content.extension === "fbx") {
                // Lazy-load FBXLoader chunk (M2 Plan-1.9-Phase-1-CP2).
                import('three/examples/jsm/loaders/FBXLoader.js').then(({ FBXLoader }) => {
                    const fbxLoader = new FBXLoader();
                    fbxLoader.load(url, objLoaded);
                }).catch((err) => {
                    logError(new Error(`Failed to load FBXLoader chunk: ${err}`));
                    this.layerIsLoading[layerId] = false;
                    this.props.renderer.loadedLayer(layerId, url);
                });
            }

            // GLTF / GLB — modern Web 3D format. Smaller than FBX, supports
            // PBR materials and embedded animations.
            if (content.extension === "gltf" || content.extension === "glb") {
                // Lazy-load GLTFLoader chunk (M2 Plan-1.9-Phase-1-CP2): only fetched
                // when an object3d-Layer with .gltf/.glb extension is instantiated.
                import('three/examples/jsm/loaders/GLTFLoader.js').then(({ GLTFLoader }) => {
                    const gltfLoader = new GLTFLoader();
                    gltfLoader.load(
                        url,
                        (gltf) => {
                            const innerScene = gltf.scene || gltf.scenes?.[0];
                            if (!innerScene) {
                                logError(new Error(`GLTF '${url}' has no scene`));
                                this.layerIsLoading[layerId] = false;
                                this.props.renderer.loadedLayer(layerId, url);
                                return;
                            }

                            // Auto-fit: GLTF models come in wildly different scales
                            // (Duck = 1.6 units, Avocado = 0.05, Fox = 250).
                            // Normalize so the largest dimension fits ~1500 world
                            // units in the 4096-unit slide coord system.
                            //
                            // CRITICAL: the renderer's animation loop overwrites
                            // layerMesh.scale every frame from the keyframe values.
                            // So we wrap the auto-fitted model in an outer Group:
                            // - outer = the layerMesh, scale stays at [1,1,1] from
                            //   the animation system
                            // - inner = the actual scene with the auto-fit scale
                            const TARGET_SIZE = 1500;
                            const bbox = new THREE.Box3().setFromObject(innerScene);
                            const size = new THREE.Vector3();
                            bbox.getSize(size);
                            const maxDim = Math.max(size.x, size.y, size.z);

                            const wrapper = new THREE.Group();
                            if (maxDim > 0) {
                                const fitScale = TARGET_SIZE / maxDim;
                                innerScene.scale.setScalar(fitScale);
                                // Recenter on origin (some models are offset)
                                const center = new THREE.Vector3();
                                bbox.getCenter(center);
                                innerScene.position.sub(center.multiplyScalar(fitScale));
                            }
                            wrapper.add(innerScene);

                            // Stash GLTF animations on the wrapper so they can be
                            // hooked up to scroll progress later if needed.
                            if (gltf.animations && gltf.animations.length > 0) {
                                (wrapper as TODOAny).gltfAnimations = gltf.animations;
                            }
                            objLoaded(wrapper);
                        },
                        undefined,
                        (error) => {
                            logError(new Error(`Failed to load GLTF '${url}': ${error}`));
                            this.layerIsLoading[layerId] = false;
                            this.props.renderer.loadedLayer(layerId, url);
                        },
                    );
                }).catch((err) => {
                    logError(new Error(`Failed to load GLTFLoader chunk: ${err}`));
                    this.layerIsLoading[layerId] = false;
                    this.props.renderer.loadedLayer(layerId, url);
                });
            }
        }
    }

    updateLight(layerId: string, content: LightContent) {
        if (!this.webGlScene) {
            throw new Error("Called RendererWebGL#updateLight before calling init.");
        }

        const light: THREE.PointLight | THREE.DirectionalLight = this.lightState[layerId];
        const cachedLayerContent = this.layerContentCache[layerId];
        const lightContent: LightContent = content;
        const contentChange = cachedLayerContent !== lightContent;
        const isDirty = !light || contentChange;

        this.layerContentCache[layerId] = lightContent;

        const SPHERE_SIZE = 100;

        if (isDirty) {
            const color = new THREE.Color(lightContent.color.r, lightContent.color.g, lightContent.color.b);

            let newLight: THREE.PointLight | THREE.SpotLight | THREE.DirectionalLight;
            if (lightContent.type === LightType.pointLight) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const pointLightContent: PointLightContent = content as any;
                newLight = new THREE.PointLight(
                    color,
                    pointLightContent.intensity,
                    pointLightContent.distance,
                    pointLightContent.decay
                );
                newLight.castShadow = lightContent.castShadow;
            }

            if (lightContent.type === LightType.spotLight) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const spotLightContent: SpotLightContent = content as any;
                newLight = new THREE.SpotLight(
                    color,
                    spotLightContent.intensity,
                    spotLightContent.distance,
                    spotLightContent.angle,
                    spotLightContent.penumbra,
                    spotLightContent.decay
                );
                newLight.castShadow = lightContent.castShadow;
                newLight.shadow.mapSize.width = 1024;
                newLight.shadow.mapSize.height = 1024;

                newLight.shadow.camera.near = 0.5;
                newLight.shadow.camera.far = 2000;
                newLight.shadow.camera.fov = 30;
            }

            if (lightContent.type === LightType.directionalLight) {
                newLight = new THREE.DirectionalLight(color, lightContent.intensity);
                newLight.castShadow = lightContent.castShadow;
            }

            this.lightState[layerId] = newLight! as TODOAny;
            this.layerMeshes[layerId] = newLight! as TODOAny;
            this.props.renderer.loadedLayer(layerId, "light");
        }

        const lightHelper = this.lightHelpers[layerId];
        const lightShadowHelper = this.lightShadowHelpers[layerId];

        if (!this.props.renderer.showLights && lightHelper) {
            this.webGlScene.remove(lightHelper);
            this.webGlScene.remove(lightShadowHelper);
            this.lightHelpers[layerId] = null;
            this.lightShadowHelpers[layerId] = null;
        }

        if (this.props.renderer.showLights && (isDirty || !lightHelper)) {
            this.webGlScene.remove(lightHelper);
            this.webGlScene.remove(lightShadowHelper);
            const layerLightState = this.lightState[layerId];
            let newLightHelper: THREE.PointLightHelper | THREE.SpotLightHelper | THREE.DirectionalLightHelper;
            switch (lightContent.type) {
                case LightType.pointLight:
                    newLightHelper = new THREE.PointLightHelper(layerLightState as THREE.PointLight, SPHERE_SIZE);
                    break;
                case LightType.spotLight:
                    newLightHelper = new THREE.SpotLightHelper(layerLightState as unknown as THREE.SpotLight);
                    break;
                case LightType.directionalLight:
                    newLightHelper = new THREE.DirectionalLightHelper(
                        layerLightState as THREE.DirectionalLight,
                        SPHERE_SIZE
                    );
                    break;
                default:
            }
            const newLightShadowHelper = new THREE.CameraHelper(light.shadow.camera);
            this.webGlScene.add(newLightHelper!);
            this.webGlScene.add(newLightShadowHelper);
            newLightHelper!.update();
            newLightShadowHelper.update();
            this.lightHelpers[layerId] = newLightHelper!;
            this.lightShadowHelpers[layerId] = newLightShadowHelper;
        }
    }

    updateHTMLSvg(
        layerId: string,
        content: SvgContent,
        timer: number,
        forceReload = false,
    ) {
        const existingOverlay = this.svgOverlays[layerId];
        const cachedLayerContent = this.layerContentCache[layerId] as SvgContent | undefined;
        const url = getLayerContentUrl(content);
        const cachedUrl = cachedLayerContent
            ? getLayerContentUrl(cachedLayerContent)
            : null;
        const contentChange = cachedUrl !== url;
        const isDirty = forceReload || !existingOverlay || contentChange;
        // Using different key than usual to avoid sharing the same
        // layerIsLoading entry with updateSvg, since isDirty does not take
        // asHtml changes into account.
        const isLoadingKey = `${layerId}html`;
        const isLoading = this.layerIsLoading[isLoadingKey];

        if (isDirty && !isLoading) {
            this.layerIsLoading[isLoadingKey] = true;
            fetch(url)
                .then((res) => res.text())
                .then((rawSvg) => {
                    // Strip ALL full-width background/cover rects in the SVG.
                    // The background color comes from the CSS container, not the SVG.
                    const svg = rawSvg.replace(
                        /(<rect\s[^>]*width="1080"[^>]*fill="[^"]*")(\s*fill-opacity="[^"]*")?(\s*\/>)/g,
                        '$1 fill-opacity="0"$3',
                    );
                    const element = this.svgOverlays[layerId] || document.createElement("div") as HTMLDivElement;
                    element.style.position = 'absolute';
                    element.style.top = '0';
                    element.style.left = '0';
                    element.style.width = '100%';
                    element.style.height = '100%';
                    element.style.pointerEvents = 'none';
                    element.style.transformOrigin = 'center center';
                    element.style.display = 'none'; // hidden until renderLayerHTML shows it
                    element.innerHTML = svg;
                    // Ensure the inner SVG scales to fill the overlay container
                    const svgEl = element.querySelector('svg');
                    if (svgEl) {
                        svgEl.setAttribute('width', '100%');
                        svgEl.setAttribute('height', '100%');
                        svgEl.style.display = 'block';
                    }
                    // Auto-register svgTriggers for phase-* groups
                    // This enables scroll_bindings to show/hide SVG phases
                    if (svgEl) {
                        const triggers: Record<string, () => void> = {};
                        const phaseGroups = svgEl.querySelectorAll('[id^="phase-"]');
                        phaseGroups.forEach((group) => {
                            const g = group as SVGElement;
                            const phaseId = g.getAttribute('id')!;
                            // Store initial opacity for reset
                            const initialOpacity = g.getAttribute('opacity') || '1';
                            g.setAttribute('data-initial-opacity', initialOpacity);
                            g.style.opacity = initialOpacity;

                            const triggerName = `show-${phaseId}`;
                            triggers[triggerName] = () => {
                                g.style.transition = 'opacity 0.8s ease-in-out';
                                g.style.opacity = '1';
                            };
                        });
                        if (Object.keys(triggers).length > 0) {
                            (svgEl as any).svgTriggers = triggers;
                        }
                    }

                    if (!this.svgOverlays[layerId]) {
                        this.cssRendererDom.appendChild(element);
                    }
                    this.svgOverlays[layerId] = element;
                    this.layerContentCache[layerId] = content;
                    this.layerIsLoading[isLoadingKey] = false;
                    if (this.props.isCoverActive?.()) {
                        element.style.display = 'none';
                    }
                    this.props.renderer.loadedLayer(layerId, url);
                })
                .catch((error) => {
                    logError(new Error(`Failed to load SVG image from '${url}': ${error}`));
                    this.layerIsLoading[isLoadingKey] = false;
                    this.props.renderer.loadedLayer(layerId, url);
                });
        }

        // Reset legacy .visible classes wenn timer zurückgesetzt
        if (timer === 0 && this.layerScrollBindingsFired[layerId]) {
            this.layerScrollBindingsFired[layerId] = new Set();
            const element = this.svgOverlays[layerId];
            if (element) {
                element.querySelectorAll('.visible').forEach(el => {
                    el.classList.remove('visible');
                });
            }
        }

        // Scroll-driven SVG phase animation system.
        // Supports two animation types per phase group:
        // 1. Opacity fade (for text, labels, shapes) — elements fade in/out
        // 2. Stroke drawing (for polyline, path, line) — lines draw themselves
        //
        // Both are continuously proportional to scroll position and fully reversible.
        const scrollBindings = content.scroll_bindings || (content as any).scrollBindings;
        if (scrollBindings && scrollBindings.length > 0) {
            const element = this.svgOverlays[layerId];
            if (element) {
                const svgEl = element.querySelector('svg');
                if (svgEl) {
                    // Honour reduced-motion once per frame: data-anim elements
                    // jump straight to their end state (see the data-anim block).
                    const reduceMotion =
                        typeof window !== 'undefined' &&
                        typeof window.matchMedia === 'function' &&
                        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
                    const phaseGroups = svgEl.querySelectorAll('[id^="phase-"]');
                    for (const group of phaseGroups) {
                        const g = group as SVGElement;
                        const phaseId = g.getAttribute('id')!;
                        const triggerName = `show-${phaseId}`;

                        const binding = scrollBindings.find(
                            (b: { trigger: string }) => b.trigger === triggerName
                        );

                        if (!binding) {
                            // No binding = always visible (phase-1)
                            // Do NOT animate strokes — phase-1 contains static elements
                            // like axes, labels, headlines that should appear immediately
                            g.style.opacity = '1';
                            // Reset any stroke-dashoffset from previous animations
                            g.querySelectorAll('polyline, path, line').forEach((el) => {
                                const svgEl = el as SVGGeometryElement;
                                if (svgEl.dataset.totalLength) {
                                    svgEl.style.strokeDashoffset = '0';
                                }
                            });
                            continue;
                        }

                        // Phase reveal range. Default fade is 1500 scroll
                        // units, but a phase can opt into a longer window
                        // via data-fade-duration (used by typewriter, which
                        // needs ~6000 units to type 30 words at a readable
                        // pace).
                        const fadeStart = binding.at;
                        const customFade = parseFloat(
                            g.getAttribute('data-fade-duration') || '0',
                        );
                        const fadeDuration = customFade > 0 ? customFade : 1500;
                        const fadeEnd = fadeStart + fadeDuration;
                        const isTypewriterPhase = g.hasAttribute('data-typewriter');
                        // Animated phases (data-anim) drive their own per-element
                        // opacity, so the group itself must stay fully opaque.
                        const hasDataAnim = g.querySelector('[data-anim]') !== null;
                        // Stroke drawing range (longer, covers the full phase)
                        const strokeDuration = 3000;

                        let progress: number;
                        if (timer < fadeStart) {
                            progress = 0;
                        } else if (timer >= fadeEnd) {
                            progress = 1;
                        } else {
                            progress = (timer - fadeStart) / fadeDuration;
                        }
                        progress = Math.min(1, Math.max(0, progress));

                        // Opacity for non-stroke elements (text, rect, circle).
                        // Typewriter phase stays fully opaque — the per-word
                        // visibility hook (below) drives the reveal instead,
                        // so words appear at full strength as they're typed.
                        g.style.opacity =
                            isTypewriterPhase || hasDataAnim ? '1' : String(progress);
                        g.style.transition = 'none';

                        // Stroke drawing for lines/paths within this phase
                        this.animatePhaseStrokes(g, timer, fadeStart, strokeDuration);

                        // Bubble grow-in animation: animate radius from 0 to target
                        // Eased with cubic-out for satisfying "pop into view" feel
                        const bubbleCircles = g.querySelectorAll('circle[data-bubble-r]');
                        if (bubbleCircles.length > 0) {
                            const easedProgress = 1 - Math.pow(1 - progress, 3);
                            bubbleCircles.forEach((c) => {
                                const targetR = parseFloat((c as SVGCircleElement).getAttribute('data-bubble-r') || '0');
                                const currentR = targetR * easedProgress;
                                (c as SVGCircleElement).setAttribute('r', String(currentR));
                            });
                        }

                        // Typewriter word-by-word reveal: <text> elements with
                        // data-typewriter-index inside a phase group are shown
                        // progressively as the user scrolls past the phase
                        // trigger. visibleCount = floor(progress * totalWords),
                        // so scroll-speed determines typing speed naturally.
                        // Matches both <text> and <tspan> with data-typewriter-index.
                        // Lines+tspans is the production layout (browser handles
                        // word spacing); per-text fallback kept for old SVGs.
                        const typewriterWords = g.querySelectorAll('[data-typewriter-index]');
                        if (typewriterWords.length > 0) {
                            const totalWords = typewriterWords.length;
                            const visibleCount = Math.min(
                                totalWords,
                                Math.floor(progress * (totalWords + 0.5)),
                            );
                            typewriterWords.forEach((el, i) => {
                                (el as SVGElement).style.visibility =
                                    i < visibleCount ? 'visible' : 'hidden';
                            });
                        }

                        // Generic declarative per-element motion (data-anim) —
                        // ported from Studio-Motion-Live.html; a pure function of
                        // this phase's `progress` (deterministic + reversible).
                        // Vocabulary (count/rise/scale/grow-x) lives in dataAnim.ts.
                        applyDataAnim(g, progress, reduceMotion);
                    }

                    // ── Camera pan/zoom across phases ───────────────
                    // If the SVG has a #camera-viewport element, the phases
                    // inside it may carry data-camera-bbox="x y w h" to drive
                    // a cinematic pan-and-zoom. The camera starts at the full
                    // map extent, then linearly interpolates to each region's
                    // bbox during that phase's fade. Between phases the camera
                    // holds the last bbox.
                    const cameraViewport = svgEl.querySelector('#camera-viewport') as SVGGElement | null;
                    if (cameraViewport) {
                        const MAP_X = 100;
                        const MAP_Y = 440;
                        const MAP_W = 880;
                        const MAP_H = 1280;
                        const fullBBox: [number, number, number, number] = [MAP_X, MAP_Y, MAP_W, MAP_H];

                        interface CamPhase {
                            bbox: [number, number, number, number];
                            at: number;
                            fadeDuration: number;
                        }
                        const camPhases: CamPhase[] = [];
                        const camPhaseGroups = cameraViewport.querySelectorAll('[data-camera-bbox]');
                        for (const cp of camPhaseGroups) {
                            const id = cp.getAttribute('id');
                            if (!id) continue;
                            const b = cp.getAttribute('data-camera-bbox')!
                                .split(/\s+/).map(parseFloat);
                            if (b.length !== 4 || b.some(isNaN)) continue;
                            const binding = scrollBindings.find(
                                (bb: { trigger: string }) => bb.trigger === `show-${id}`,
                            );
                            if (!binding) continue;
                            const customFade = parseFloat(cp.getAttribute('data-fade-duration') || '0');
                            camPhases.push({
                                bbox: [b[0], b[1], b[2], b[3]],
                                at: binding.at,
                                fadeDuration: customFade > 0 ? customFade : 1500,
                            });
                        }
                        camPhases.sort((a, b) => a.at - b.at);

                        if (camPhases.length > 0) {
                            // Ease-in-out cubic for smooth pan
                            const ease = (t: number) =>
                                t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
                            const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
                            const lerpBBox = (
                                a: [number, number, number, number],
                                b: [number, number, number, number],
                                t: number,
                            ): [number, number, number, number] => [
                                lerp(a[0], b[0], t), lerp(a[1], b[1], t),
                                lerp(a[2], b[2], t), lerp(a[3], b[3], t),
                            ];

                            let currentBBox: [number, number, number, number] = fullBBox;
                            for (let i = 0; i < camPhases.length; i++) {
                                const cp = camPhases[i];
                                const fadeEnd = cp.at + cp.fadeDuration;
                                if (timer >= fadeEnd) {
                                    currentBBox = cp.bbox;
                                    continue;
                                }
                                if (timer <= cp.at) {
                                    currentBBox = i === 0 ? fullBBox : camPhases[i - 1].bbox;
                                    break;
                                }
                                const t = ease((timer - cp.at) / cp.fadeDuration);
                                const prev = i === 0 ? fullBBox : camPhases[i - 1].bbox;
                                currentBBox = lerpBBox(prev, cp.bbox, t);
                                break;
                            }

                            // Compute scale so bbox fits in the map area, then
                            // translate so bbox center aligns with map center.
                            const scaleX = MAP_W / currentBBox[2];
                            const scaleY = MAP_H / currentBBox[3];
                            const scale = Math.min(scaleX, scaleY);
                            const targetCx = MAP_X + MAP_W / 2;
                            const targetCy = MAP_Y + MAP_H / 2;
                            const bboxCx = currentBBox[0] + currentBBox[2] / 2;
                            const bboxCy = currentBBox[1] + currentBBox[3] / 2;
                            const tx = targetCx - bboxCx * scale;
                            const ty = targetCy - bboxCy * scale;
                            cameraViewport.setAttribute(
                                'transform',
                                `translate(${tx.toFixed(2)} ${ty.toFixed(2)}) scale(${scale.toFixed(4)})`,
                            );

                            // Inverse-scale labels and dots so they stay at
                            // their declared pixel size even when the camera
                            // zooms in. Without this, a 5× zoom renders 18px
                            // labels as 90px monsters.
                            const invScale = 1 / scale;
                            cameraViewport.querySelectorAll('[data-base-font-size]').forEach((el) => {
                                const base = parseFloat(el.getAttribute('data-base-font-size') || '0');
                                if (base > 0) {
                                    (el as SVGTextElement).setAttribute('font-size', String(base * invScale));
                                }
                            });
                            cameraViewport.querySelectorAll('[data-base-r]').forEach((el) => {
                                const base = parseFloat(el.getAttribute('data-base-r') || '0');
                                if (base > 0) {
                                    (el as SVGCircleElement).setAttribute('r', String(base * invScale));
                                }
                            });
                        }
                    }
                }
            }
        }
    }

    // ── Interactive Map Layer ──────────────────────────────────────────────

    /**
     * Create / update a MapTiler SDK (MapLibre GL) map instance for a map layer.
     * Follows the same DOM-overlay pattern as updateHTMLSvg.
     */
    async updateMap(layerId: string, content: MapContent) {
        if (this.mapInstances[layerId]) return; // already initialised
        if (this.layerIsLoading[layerId]) return;

        this.layerIsLoading[layerId] = true;

        try {
            const maptilersdk = await import('@maptiler/sdk');
            this.mapSdk = maptilersdk; // cache for sync marker creation

            // Create container div (same pattern as SVG overlays)
            const container = document.createElement('div');
            container.style.position = 'absolute';
            container.style.top = '0';
            container.style.left = '0';
            container.style.width = '100%';
            container.style.height = '100%';
            container.style.pointerEvents = content.interactive ? 'auto' : 'none';
            container.style.display = 'none'; // hidden until renderLayerHTML shows it
            container.style.overflow = 'hidden';

            // Hide all MapLibre UI controls via scoped CSS
            const style = document.createElement('style');
            style.textContent = `
                .maplibregl-ctrl-top-left, .maplibregl-ctrl-top-right,
                .maplibregl-ctrl-bottom-left, .maplibregl-ctrl-bottom-right,
                .maplibregl-ctrl { display: none !important; }
            `;
            container.appendChild(style);
            this.cssRendererDom.appendChild(container);
            this.mapContainers[layerId] = container;

            // Set API key
            maptilersdk.config.apiKey = (content as any).apiKey
                || (window as any).__MAPTILER_API_KEY__
                || '';

            // Initial camera from first waypoint
            const firstWp = content.waypoints[0];
            const map = new maptilersdk.Map({
                container,
                style: maptilersdk.MapStyle[content.style?.toUpperCase?.() as keyof typeof maptilersdk.MapStyle]
                    || content.style
                    || maptilersdk.MapStyle.STREETS,
                center: [firstWp?.lng ?? 0, firstWp?.lat ?? 0],
                zoom: firstWp?.zoom ?? 2,
                bearing: firstWp?.bearing ?? 0,
                pitch: firstWp?.pitch ?? 0,
                interactive: content.interactive ?? false,
                // MapLibre GL v4 removed `false` from AttributionControlOptions' type
                // union, but runtime still accepts the boolean to disable the control.
                // Cast to keep the runtime-correct config without widening types elsewhere.
                attributionControl: false as unknown as object,
            });

            // Remove all default UI controls (zoom, compass, geolocate)
            map.on('load', () => {
                // Remove navigation control if present
                const controls = (map as any)._controls || [];
                for (const ctrl of [...controls]) {
                    try { map.removeControl(ctrl); } catch { /* some are not removable */ }
                }
            });

            this.mapInstances[layerId] = map;
            this.layerIsLoading[layerId] = false;
            this.props.renderer.loadedLayer(layerId, `map:${content.style}`);
        } catch (error) {
            logError(new Error(`Failed to load MapTiler SDK for map layer '${layerId}': ${error}`));
            this.layerIsLoading[layerId] = false;
            this.props.renderer.loadedLayer(layerId, `map:error`);
        }
    }

    /**
     * Per-frame update: drive map camera position from scroll timer via waypoint interpolation.
     * Called from renderLayerHTML when a map layer is encountered.
     */
    renderMapLayer(
        layer: LayerRender,
        parent: CSS3DObject | null,
        timer: number,
    ) {
        const container = this.mapContainers[layer.id];
        const map = this.mapInstances[layer.id];
        if (!container || !map) return;

        const content = layer.content as MapContent;
        const isInLifetime = layer.from <= timer && layer.to >= timer;
        const visible = (this.visibilityOverrides[layer?.id] ?? layer?.visible ?? true) && isInLifetime;

        if (!visible) {
            container.style.display = 'none';
            return;
        }

        const layer3D = layer.layer3D(timer);
        const parentOpacity = parent ? parent.userData?.opacity || 1 : 1;
        const effectiveOpacity = (layer3D.opacity ?? 1) * parentOpacity;

        if (effectiveOpacity === 0) {
            container.style.display = 'none';
            return;
        }

        // Accumulate transforms from parent (same pattern as SVG overlays)
        const parentScale = parent?.userData?.scale || [1, 1, 1];
        const scaleX = (layer3D.scale?.[0] ?? 1) * parentScale[0];
        const scaleY = (layer3D.scale?.[1] ?? 1) * parentScale[1];
        const parentPos = parent?.userData?.position || [0, 0];
        const posX = ((layer3D.position?.[0] ?? 0) + parentPos[0]) * 100;
        const posY = ((layer3D.position?.[1] ?? 0) + parentPos[1]) * -100;
        const parentRotation = parent?.userData?.rotation || [0, 0, 0];
        const rotationZ = (layer3D.rotation?.[2] ?? 0) + parentRotation[2];

        const transforms: string[] = [];
        if (posX !== 0 || posY !== 0) transforms.push(`translate(${posX}%, ${posY}%)`);
        if (scaleX !== 1 || scaleY !== 1) transforms.push(`scale(${scaleX}, ${scaleY})`);
        if (rotationZ !== 0) transforms.push(`rotate(${rotationZ}deg)`);

        container.style.display = 'block';
        container.style.opacity = `${effectiveOpacity}`;
        container.style.transform = transforms.join(' ');
        container.style.transformOrigin = 'center center';

        // Drive map camera from scroll position.
        // jumpTo() queues the camera change; triggerRepaint() forces MapLibre
        // to actually render it (its internal rAF loop may be idle when
        // interactive=false). resize() is needed after the container goes
        // from display:none to display:block so MapLibre knows the real size.
        const camera = interpolateWaypoints(content.waypoints, timer);
        try {
            // Resize once when container first becomes visible
            if (!(container as any).__mapResized) {
                map.resize();
                (container as any).__mapResized = true;
            }
            map.jumpTo({
                center: [camera.lng, camera.lat],
                zoom: camera.zoom,
                bearing: camera.bearing,
                pitch: camera.pitch,
            });
            map.triggerRepaint();
        } catch {
            // Map not ready yet — silently skip this frame
        }

        // Update markers (show markers for the closest waypoint)
        const activeMarkers = getActiveMarkers(content.waypoints, timer);
        const markerKey = `${layer.id}_markers`;
        const existingMarkers = (this as any)[markerKey] as any[] | undefined;
        const markerHash = JSON.stringify(activeMarkers);
        const prevHash = (this as any)[`${markerKey}_hash`] as string | undefined;

        if (markerHash !== prevHash) {
            // Remove old markers
            if (existingMarkers) {
                existingMarkers.forEach((m: any) => m.remove());
            }
            // Add new markers (lazy — only if maptilersdk is loaded)
            const newMarkers: any[] = [];
            if (this.mapSdk) {
                for (const mk of activeMarkers) {
                    try {
                        const el = document.createElement('div');
                        el.style.width = '14px';
                        el.style.height = '14px';
                        el.style.borderRadius = '50%';
                        el.style.backgroundColor = mk.color ? `#${mk.color.replace('#', '')}` : '#BA7517';
                        el.style.border = '2px solid white';
                        el.style.boxShadow = '0 1px 4px rgba(0,0,0,0.3)';
                        const marker = new this.mapSdk.Marker({ element: el });
                        marker.setLngLat([mk.lng, mk.lat]).addTo(map);
                        newMarkers.push(marker);
                    } catch {
                        // Marker creation failed silently
                    }
                }
            }
            (this as any)[markerKey] = newMarkers;
            (this as any)[`${markerKey}_hash`] = markerHash;
        }
    }

    /**
     * Animate stroke-dashoffset on all stroked elements within a phase group.
     * Lines draw themselves proportionally to the scroll position.
     */
    private animatePhaseStrokes(group: SVGElement, timer: number, startAt: number, duration: number) {
        const strokeElements = group.querySelectorAll('polyline, path, line');
        for (const el of strokeElements) {
            const svgEl = el as SVGGeometryElement;

            // Skip elements without stroke (fills only)
            const stroke = svgEl.getAttribute('stroke') || svgEl.style.stroke;
            if (!stroke || stroke === 'none') continue;

            // Calculate total length (cache it)
            if (!svgEl.dataset.totalLength) {
                try {
                    const len = svgEl.getTotalLength();
                    if (len > 0) {
                        svgEl.dataset.totalLength = String(len);
                        svgEl.style.strokeDasharray = String(len);
                        svgEl.style.strokeDashoffset = String(len);
                    }
                } catch {
                    continue; // getTotalLength not supported on this element
                }
            }

            const totalLength = parseFloat(svgEl.dataset.totalLength || '0');
            if (totalLength <= 0) continue;

            // Calculate draw progress
            let drawProgress: number;
            if (timer < startAt) {
                drawProgress = 0;
            } else if (timer >= startAt + duration) {
                drawProgress = 1;
            } else {
                drawProgress = (timer - startAt) / duration;
            }
            drawProgress = Math.min(1, Math.max(0, drawProgress));

            // Set stroke-dashoffset: totalLength (hidden) → 0 (fully drawn)
            svgEl.style.strokeDashoffset = String(totalLength * (1 - drawProgress));
        }
    }

    getJson(jsonData: PostJson) {
        this.templateJson = jsonData;

        // Build visibility override map from JSON for the animate-loop.
        // The PlayablePresentation tree is immutable after creation, so we
        // store overrides separately and check them in processLayer().
        // We must always write the explicit value (not just delete on true)
        // because the PlayablePresentation may have been constructed with
        // visible=false for layers that were saved as hidden — deleting the
        // override would fall back to that stale false.
        const jsonLayers = jsonData?.data?.data?.layers;
        if (Array.isArray(jsonLayers)) {
            for (const jl of jsonLayers) {
                if (jl?.id && jl.visible === false) {
                    this.visibilityOverrides[jl.id] = false;
                } else if (jl?.id) {
                    this.visibilityOverrides[jl.id] = true;
                }
            }

            const bloomSvg = jsonLayers.find(
                (l) => l?.id === 'svg-layer-1' && l.type === LayerType.svg,
            );
            if (bloomSvg?.content) {
                const content = bloomSvg.content as SvgContent;
                const url = getLayerContentUrl(content);
                const cached = this.layerContentCache['svg-layer-1'] as SvgContent | undefined;
                const cachedUrl = cached ? getLayerContentUrl(cached) : null;
                const asHtml = Boolean(
                    content.asHtml || (bloomSvg as { asHtml?: boolean }).asHtml,
                );
                if (asHtml && url && url !== cachedUrl) {
                    const timer = this.props.slidePlayer?.timer ?? 0;
                    this.updateHTMLSvg('svg-layer-1', content, timer, true);
                }
            }
        }
    }

    getFonts(_fontFamilies: CustomFonts, _liveUpdate: boolean = false) {
        // Pre-warm troika-three-text's SDF font atlas for the default font
        // and all standard fonts. Without this, the first text render uses a
        // system fallback font while troika loads the .ttf asynchronously,
        // causing a visible font flash (especially noticeable when images load
        // fast via local imgproxy). Preloading ensures the atlas is ready
        // before the first updateText() call.
        const fontsToPreload = new Set<string>();
        fontsToPreload.add(DEFAULT_FONT);
        for (const f of Object.values(FONTS)) {
            fontsToPreload.add(f);
        }
        for (const font of fontsToPreload) {
            preloadFont({ font, characters: 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789.,;:!?-–—' }, () => {
                // Font atlas ready — no action needed, troika picks it up
                // automatically on next sync() call.
            });
        }
    }

    updateText(layerId: string, content: TextContent, liveUpdate: boolean = false) {
        /* eslint-disable */
        const animatable3DLayer = this.layerState3d[layerId];
        const cachedLayerContent = this.layerContentCache[layerId];
        let contentChange = cachedLayerContent !== content;
        const isDirty = !animatable3DLayer || contentChange;
        const isLoading = this.layerIsLoading[layerId];

        if (liveUpdate) {
            WebGLAssets.setGpuTextOnStart = true;

            let txtObj3d = this.layerMeshes[layerId] as THREE.Mesh;
            if (txtObj3d) {
                const textContent: TextContent = content;
                let getText = textContent.text;
                // Dispose old troika Text mesh before removing — prevents SDF texture leak
                const oldText = txtObj3d.children[0] as any;
                if (oldText) {
                    if (oldText.dispose) oldText.dispose();
                    if (oldText.material) {
                        if (oldText.material.map) oldText.material.map.dispose();
                        oldText.material.dispose();
                    }
                }
                txtObj3d.remove(oldText);

                const fontFamily = FONTS[textContent.family as keyof typeof FONTS] || DEFAULT_FONT;

                let cFont = null;

                if (this.templateJson?.data?.data?.presentationOptions?.slideOptions?.fontMapping?.text?.family) {
                    let i: number = 0;
                    for (i; i < this.templateJson.data.data.presentationOptions.slideOptions.fontInfos?.length; i++) {
                        if (
                            this.templateJson.data.data.presentationOptions.slideOptions.fontInfos[i].familyName ===
                            this.templateJson.data.data.presentationOptions.slideOptions.fontMapping.text.family
                        ) {
                            cFont = this.templateJson.data.data.presentationOptions.slideOptions.fontInfos[i].fontUrl;
                        }
                    }
                }

                if (!cFont) cFont = fontFamily;

                const text = new Text();
                text.textAlign = "left";
                text.text = getText
                    .split("\n")
                    .map((l) => l.trim())
                    .join("\n");
                text.font = cFont;
                text.fontSize = textContent.size;
                text.lineHeight = textContent.lineHeight;
                text.maxWidth = textContent.useWidth ? textContent.width : Infinity;
                text.whiteSpace = textContent.mode === "nowrap" ? "nowrap" : "normal";
                text.letterSpacing = textContent.letterSpacing;

                text.material = new THREE.MeshBasicMaterial({
                    color: colorToHex(threeToColor(textContent.color)),
                    transparent: true,
                });
                text.material.needsUpdate = true;

                // TODO clean up with text.dispose()
                text.anchorX = textContent.xAlign;
                text.anchorY = textContent.yAlign === "center" ? "middle" : textContent.yAlign;

                const scaleFactor = 2.0;
                text.scale.set(scaleFactor, scaleFactor, scaleFactor);

                text.sync(() => {
                    if (this.webGLRenderer && this.webGlScene && this.camera) {
                        this.webGLRenderer.render(this.webGlScene, this.camera);
                    }
                });

                txtObj3d.add(text);
                liveUpdate = false;
            }
        }

        if (!WebGLAssets.setGpuTextOnStart) {
            const objLoaded = (obj: TODOAny) => {
                this.props.renderer.loadedLayer(layerId, content.text);
                this.layerState3d[layerId] = obj;
                this.layerIsLoading[layerId] = false;
                // cache content
                this.layerContentCache[layerId] = content;
                this.layerMeshes[layerId] = obj;
            };

            if (isDirty && !isLoading) {
                this.layerIsLoading[layerId] = true;

                const textContent: TextContent = content;
                let fontFamily = FONTS[textContent.family as keyof typeof FONTS] || DEFAULT_FONT;

                let cFont = null;

                //console.log(this.templateJson.data.data.presentationOptions.slideOptions)

                if (this.templateJson?.data?.data?.presentationOptions?.slideOptions?.fontMapping?.text?.family) {
                    //console.log("checking for font");
                    //console.log(this.templateJson.data.data.presentationOptions.slideOptions.fontMapping.text.family);

                    if (
                        this.templateJson.data.data.presentationOptions.slideOptions.fontMapping.text.family
                            .toString()
                            .indexOf("Italianno-Regular", 0) !== -1
                    ) {
                        cFont = fontItalianno;
                    } else if (
                        this.templateJson.data.data.presentationOptions.slideOptions.fontMapping.text.family
                            .toString()
                            .indexOf("JetBrainsMono", 0) !== -1
                    ) {
                        cFont = fontJetBrains;
                    } else if (
                        this.templateJson.data.data.presentationOptions.slideOptions.fontMapping.text.family
                            .toString()
                            .indexOf("Merriweather-Variable", 0) !== -1
                    ) {
                        cFont = fontMerriweather;
                    } else if (
                        this.templateJson.data.data.presentationOptions.slideOptions.fontMapping.text.family
                            .toString()
                            .indexOf("MerriweatherSans", 0) !== -1
                    ) {
                        cFont = fontMerriweatherSans;
                    } else if (
                        this.templateJson.data.data.presentationOptions.slideOptions.fontMapping.text.family
                            .toString()
                            .indexOf("NotoSans", 0) !== -1
                    ) {
                        cFont = fontNotoSans;
                    } else if (
                        this.templateJson.data.data.presentationOptions.slideOptions.fontMapping.text.family
                            .toString()
                            .indexOf("NotoSerif", 0) !== -1
                    ) {
                        cFont = fontNotoSerif;
                    } else if (
                        this.templateJson.data.data.presentationOptions.slideOptions.fontMapping.text.family
                            .toString()
                            .indexOf("PortLligatSlab-Regular", 0) !== -1
                    ) {
                        cFont = fontPortLligatSlab;
                    } else if (
                        this.templateJson.data.data.presentationOptions.slideOptions.fontMapping.text.family
                            .toString()
                            .indexOf("RobotoCondensed-Variable", 0) !== -1
                    ) {
                        cFont = fontRoboto;
                    } else if (
                        this.templateJson.data.data.presentationOptions.slideOptions.fontMapping.text.family
                            .toString()
                            .indexOf("SourceSans3-Variable", 0) !== -1
                    ) {
                        cFont = fontSourceSans3;
                    } else if (
                        this.templateJson.data.data.presentationOptions.slideOptions.fontMapping.text.family
                            .toString()
                            .indexOf("SourceSerif4-Variable", 0) !== -1
                    ) {
                        cFont = fontSourceSerif4;
                    } else {
                        let i: number = 0;
                        for (
                            i;
                            i < this.templateJson.data.data.presentationOptions.slideOptions.fontInfos?.length;
                            i++
                        ) {
                            //console.log(this.templateJson.data.data.presentationOptions.slideOptions.fontInfos[i])
                            if (
                                this.templateJson.data.data.presentationOptions.slideOptions.fontInfos[i].familyName ===
                                this.templateJson.data.data.presentationOptions.slideOptions.fontMapping.text.family
                            ) {
                                //console.log("found font");
                                cFont =
                                    this.templateJson.data.data.presentationOptions.slideOptions.fontInfos[i].fontUrl;
                                //console.log("cFont");
                            }
                        }
                    }
                }

                if (!cFont) cFont = fontFamily;

                const text = new Text();
                text.textAlign = textContent.align;
                text.text = textContent.text
                    .split("\n")
                    .map((l) => l.trim())
                    .join("\n");
                text.font = cFont;

                text.fontSize = textContent.size;
                text.lineHeight = textContent.lineHeight;
                text.maxWidth = textContent.useWidth ? textContent.width : Infinity;
                text.whiteSpace = textContent.mode === "nowrap" ? "nowrap" : "normal";
                text.letterSpacing = textContent.letterSpacing;

                text.material = new THREE.MeshBasicMaterial({
                    color: colorToHex(threeToColor(textContent.color)),
                    side: textContent.doubleSide ? THREE.DoubleSide : THREE.FrontSide,
                    transparent: true,
                });
                text.material.needsUpdate = true;

                // TODO clean up with text.dispose()
                text.anchorX = textContent.xAlign;
                text.anchorY = textContent.yAlign === "center" ? "middle" : textContent.yAlign;

                const scaleFactor = 2.0;
                text.scale.set(scaleFactor, scaleFactor, scaleFactor);

                // Explicitly trigger troika's async SDF atlas build.
                // Without this, troika relies on Three.js's onBeforeRender
                // hook which may never fire if the render loop is stopped
                // (the editor calls stop() after initial load). The sync
                // callback re-renders the scene once the atlas is ready.
                text.sync(() => {
                    // Font atlas built — force a Three.js re-render so the
                    // correct font appears even if the animation loop is stopped.
                    if (this.webGLRenderer && this.webGlScene && this.camera) {
                        this.webGLRenderer.render(this.webGlScene, this.camera);
                    }
                });

                const textAnchor = new THREE.Object3D();
                textAnchor.add(text);

                objLoaded(textAnchor);
            }
        }
        /* eslint-enable */
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    updateSvg(layerId: string, content: any) {
        const animatable3DLayer = this.layerState3d[layerId];
        const cachedLayerContent = this.layerContentCache[layerId];
        // only reload when url changed
        const contentChange =
            // TODO Change to .key once we are sure all stories have only assets with .key
            !cachedLayerContent || content.url !== cachedLayerContent.url;
        const isDirty = !animatable3DLayer || contentChange;
        const isLoading = this.layerIsLoading[layerId];

        if (isDirty && !isLoading) {
            this.layerIsLoading[layerId] = true;
            // TODO DESCR-218: revisit
            // const url = createCdnSvgImageUrl(content);
            const url = getLayerContentUrl(content);

            const objLoaded = (obj: TODOAny) => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                this.uploadToGpu(obj as any);
                this.props.renderer.loadedLayer(layerId, url);

                this.layerState3d[layerId] = obj;
                this.layerIsLoading[layerId] = false;
                // cache content
                this.layerContentCache[layerId] = content;
                this.layerMeshes[layerId] = obj;
            };

            const svgLoader = new SVGLoader();
            svgLoader.load(
                url,
                (data) => {
                    const { paths } = data;
                    const group = new THREE.Group();

                    for (const path of paths) {
                        const shapes = path.toShapes(true);
                        for (const shape of shapes) {
                            const geometry = new THREE.ShapeGeometry(shape);
                            const material = new THREE.MeshBasicMaterial({
                                color: path.color,
                                opacity: 1.0,
                                side: THREE.DoubleSide,
                                depthWrite: false,
                                transparent: true,
                                fog: false,
                            });
                            const mesh = new THREE.Mesh(geometry, material);
                            group.add(mesh);
                        }
                    }
                    const xOffset = content.width / 2;
                    const yOffset = content.height / 2;
                    group.position.set(-xOffset, yOffset, 0);
                    const anchor = new THREE.Object3D();
                    group.rotateZ(Math.PI);
                    group.rotateY(Math.PI);
                    anchor.add(group);
                    objLoaded(anchor);
                },
                undefined, // onProgress
                (error) => {
                    console.error(`[RendererWebGL] Failed to load SVG: ${url}`, error);
                    this.layerIsLoading[layerId] = false;
                },
            );
        }
    }

    updateVideo(layerId: string, content: VideoContent, liveUpdate: boolean = false) {
        const rendererStore = this.props.renderer;
        const cachedLayerContent = this.layerContentCache[layerId];
        const { layerMeshes } = this;
        const layerContent: VideoContent = content;

        const contentChange = !isEqual(cachedLayerContent, layerContent);
        const isDirty = contentChange;
        if (liveUpdate) {
            WebGLAssets.setVideosOnStart = true;
            this.layerIsLoading[layerId] = true;
            const oldVideo = this.videoCache[layerId];
            if (oldVideo) {
                oldVideo.pause();
                oldVideo.delete(); // fully release old video element + texture
            }

            const video: Video = new Video();
            const url = getLayerContentUrl(layerContent);

            video.generateTexture(layerContent, url).then(({ material }) => {
                this.setVideoJit = true;
                // Dispose old mesh resources before replacing
                const oldMesh = layerMeshes[layerId] as THREE.Mesh;
                if (oldMesh) {
                    const oldMat = oldMesh.material as THREE.MeshBasicMaterial;
                    if (oldMat) {
                        if (oldMat.map) oldMat.map.dispose();
                        oldMat.dispose();
                    }
                    oldMesh.geometry?.dispose();
                }
                const geometry = new THREE.PlaneGeometry(1, 1);
                const getCalcWidth: number = content.calcWidth || content.width || 1;
                const getCalcHeight: number = content.calcHeight || content.height || 1;
                geometry.scale(getCalcWidth, getCalcHeight, 1);
                const mesh1: THREE.Mesh = new THREE.Mesh(geometry, material);
                layerMeshes[layerId] = mesh1;
                mesh1.userData.videoLiveUpdate = true;
                this.layerContentCache[layerId] = content;
            });
            this.videoCache[layerId] = video;
            // cache the layer
            this.layerContentCache[layerId] = content;
        }
        if (!WebGLAssets.setVideosOnStart) {
            if (isDirty && !this.layerIsLoading[layerId]) {
                this.layerIsLoading[layerId] = true;
                const oldVideo = this.videoCache[layerId];
                if (oldVideo) {
                    oldVideo.pause();
                    oldVideo.delete(); // fully release old video element + texture
                }
                const video = new Video();
                // TODO DESCR-218: revisit
                // const url = createCdnAssetUrl(layerContent);
                const url = getLayerContentUrl(layerContent);
                // always load the layer, does not mater if it fails or succeeds
                rendererStore.loadedLayer(layerId, url);

                let getCalcWidth: number = 0;
                let getCalcHeight: number = 0;

                if (layerContent.calcWidth) {
                    if (layerContent.calcWidth > 0) {
                        getCalcWidth = layerContent.calcWidth;
                        getCalcHeight = layerContent.calcHeight;
                    }
                } else {
                    getCalcWidth = layerContent.width;
                    getCalcHeight = layerContent.height;
                }

                video.generateTexture(layerContent, url).then(({ material }) => {
                    const geometry = new THREE.PlaneGeometry(1, 1);
                    geometry.scale(getCalcWidth, getCalcHeight, 1);
                    const mesh = new THREE.Mesh(geometry, material);
                    layerMeshes[layerId] = mesh;
                    this.layerIsLoading[layerId] = false;
                    this.layerContentCache[layerId] = content;
                });
                this.videoCache[layerId] = video;
                // cache the layer
                this.layerContentCache[layerId] = content;
            }
        }
    }

    updateLottie(layerId: string, content: LottieContent, liveUpdate: boolean = false) {
        const rendererStore = this.props.renderer;
        const cachedLayerContent = this.layerContentCache[layerId];
        const { layerMeshes } = this;
        const layerContent: LottieContent = content;

        const contentChange = !isEqual(cachedLayerContent, layerContent);
        const isDirty = contentChange;

        if (liveUpdate) {
            this.layerIsLoading[layerId] = true;
            const oldLottie = this.lottieCache[layerId];
            if (oldLottie) {
                oldLottie.pause();
                oldLottie.delete();
            }

            const lottiePlayer = new LottiePlayer();
            const url = getLayerContentUrl(layerContent);

            lottiePlayer.generateTexture(layerContent, url).then(({ material }) => {
                const geometry = new THREE.PlaneGeometry(1, 1);
                const getCalcWidth: number = content.calcWidth || content.width || 1;
                const getCalcHeight: number = content.calcHeight || content.height || 1;
                geometry.scale(getCalcWidth, getCalcHeight, 1);
                const mesh = new THREE.Mesh(geometry, material);
                layerMeshes[layerId] = mesh;
                mesh.userData.lottieLiveUpdate = true;
                this.layerContentCache[layerId] = content;
            });
            this.lottieCache[layerId] = lottiePlayer;
            this.layerContentCache[layerId] = content;
        }

        if (isDirty && !this.layerIsLoading[layerId]) {
            this.layerIsLoading[layerId] = true;
            const oldLottie = this.lottieCache[layerId];
            if (oldLottie) {
                oldLottie.pause();
                oldLottie.delete();
            }
            const lottiePlayer = new LottiePlayer();
            const url = getLayerContentUrl(layerContent);
            rendererStore.loadedLayer(layerId, url);

            let getCalcWidth: number = 0;
            let getCalcHeight: number = 0;

            if (layerContent.calcWidth) {
                if (layerContent.calcWidth > 0) {
                    getCalcWidth = layerContent.calcWidth;
                    getCalcHeight = layerContent.calcHeight;
                }
            } else {
                getCalcWidth = layerContent.width;
                getCalcHeight = layerContent.height;
            }

            lottiePlayer.generateTexture(layerContent, url).then(({ material }) => {
                const geometry = new THREE.PlaneGeometry(1, 1);
                geometry.scale(getCalcWidth, getCalcHeight, 1);
                const mesh = new THREE.Mesh(geometry, material);
                layerMeshes[layerId] = mesh;
                this.layerIsLoading[layerId] = false;
                this.layerContentCache[layerId] = content;
            });
            this.lottieCache[layerId] = lottiePlayer;
            this.layerContentCache[layerId] = content;
        }
    }

    updateImage(layerId: string, content: ImageContent, liveUpdate: boolean = false) {
        const rendererStore = this.props.renderer;

        const { quality } = this.props.renderer;
        const cachedLayerContent = this.layerContentCache[layerId];
        const { layerMeshes } = this;
        const layerContent: ImageContent = content;

        // Background image layers render at reduced opacity so the photo
        // acts as a subtle backdrop behind the chart SVG overlay.
        const isBgLayer = this.templateJson?.data?.data?.layers?.some(
            (l: TODOAny) => l && l.id === layerId && l.isBackground === true
        ) ?? false;

        const contentChange = !isEqual(cachedLayerContent, layerContent);
        let isDirty = contentChange;
        const layerMesh = layerMeshes[layerId];
        const qualityChanged = layerMesh && layerMesh.userData.quality !== quality!.quality;
        if (liveUpdate) {
            WebGLAssets.setImagesOnStart = true;
            isDirty = false;
            for (const i in this.videoCache) {
                this.videoCache[i].videoPlayer.pause();
            }

            let getCalcWidth: number = 0;
            let getCalcHeight: number = 0;

            if (content.calcWidth && content.calcWidth > 0) {
                getCalcWidth = content.calcWidth;
                getCalcHeight = content.calcHeight;
            } else if (content.width && content.height) {
                getCalcWidth = content.width;
                getCalcHeight = content.height;
            }

            let mesh = layerMeshes[layerId] as THREE.Mesh;

            const loader = new THREE.ImageLoader();
            const url = layerContent.url;
            if (mesh && mesh.material) {
                const liveUpdateSuccess = (imageTag: HTMLImageElement) => {
                    // Dispose old material + texture before replacing — prevents texture leak
                    const oldMaterial = mesh.material as THREE.MeshBasicMaterial;
                    if (oldMaterial) {
                        if (oldMaterial.map) oldMaterial.map.dispose();
                        oldMaterial.dispose();
                    }

                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const imageTexture: any = new THREE.Texture(imageTag);
                    imageTexture.minFilter = THREE.LinearFilter;
                    imageTexture.magFilter = THREE.LinearFilter;
                    imageTexture.needsUpdate = true;
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const getThreeColorSpace: any = THREE;
                    imageTexture.colorSpace = getThreeColorSpace.SRGBColorSpace;
                    const material = new THREE.MeshBasicMaterial({
                        map: imageTexture,
                        depthWrite: false, // remove artifacts
                        side: layerContent.doubleSide ? THREE.DoubleSide : THREE.FrontSide,
                        alphaTest: 0.01,
                        transparent: true,
                        opacity: isBgLayer ? 0.2 : 1,
                        name: String(layerId),
                    });
                    mesh.material = material;

                    if (getCalcWidth > 1) {
                        // Dispose old geometry before replacing
                        mesh.geometry?.dispose();
                        const geometry = new THREE.PlaneGeometry(1, 1);
                        geometry.scale(getCalcWidth, getCalcHeight, 1);
                        const mesh1: THREE.Mesh = new THREE.Mesh(geometry, material);
                        mesh = mesh1;
                        layerMeshes[layerId] = mesh;
                        this.layerContentCache[layerId] = content;
                    }

                    //}
                };
                loader.load(url, liveUpdateSuccess, undefined, undefined);
            }
        }
        if (!WebGLAssets.setImagesOnStart) {
            if ((isDirty || qualityChanged) && !this.layerIsLoading[layerId]) {
                this.layerIsLoading[layerId] = true;
                //console.log("layerId: " + layerId);

                const loadedImage = (
                    material: THREE.MeshBasicMaterial,
                    texture: THREE.Texture,
                    height: number,
                    width: number
                ) => {
                    // when only the quality changed, we don't create a new mesh
                    if (qualityChanged && !this.setVideoJit) {
                        const mesh = layerMeshes[layerId] as THREE.Mesh;
                        const meshMaterial = mesh.material as THREE.MeshBasicMaterial;
                        meshMaterial.map!.dispose();
                        meshMaterial.map = texture;
                        meshMaterial.needsUpdate = true;
                        mesh.geometry.scale(width / mesh.userData.width, height / mesh.userData.height, 1);
                    } else {
                        const geometry = new THREE.PlaneGeometry(1, 1);
                        geometry.scale(width, height, 1);
                        const mesh = new THREE.Mesh(geometry, material);
                        layerMeshes[layerId] = mesh;
                        // only load layer initially
                        rendererStore.loadedLayer(layerId, url);
                    }
                    layerMeshes[layerId].userData.quality = quality!.quality;
                    // we can't really capture this again
                    layerMeshes[layerId].userData.width = width;
                    layerMeshes[layerId].userData.height = height;
                    this.layerIsLoading[layerId] = false;
                    this.layerContentCache[layerId] = content;
                };
                let fallbackAttempted = false;
                const loader = new THREE.ImageLoader();

                // const qualityToUse = this.props.renderer.quality!;
                // disabled for now, as people used it wrongly!
                /*
            if (layerContent.doNotResize) {
              qualityToUse = QUALITIES.find(q => q.quality === Quality.Q4096);
            }
            */
                // TODO DESCR-218: revisit
                // const url = createCdnImageUrl(layerContent, qualityToUse.fraction * layerContent.width);
                const url = getLayerContentUrl(layerContent);
                const success = (imageTag: HTMLImageElement) => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const imageTexture: any = new THREE.Texture(imageTag);
                    imageTexture.minFilter = THREE.LinearFilter;
                    imageTexture.magFilter = THREE.LinearFilter;
                    imageTexture.needsUpdate = true;
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const getThreeColorSpace: any = THREE;
                    imageTexture.colorSpace = getThreeColorSpace.SRGBColorSpace;
                    const material = new THREE.MeshBasicMaterial({
                        map: imageTexture,
                        depthWrite: false, // remove artifacts
                        side: layerContent.doubleSide ? THREE.DoubleSide : THREE.FrontSide,
                        alphaTest: 0.01,
                        transparent: true,
                        opacity: isBgLayer ? 0.2 : 1,
                        name: String(layerId),
                    });
                    //console.log("materialname: " + material.name);
                    WebGLAssets.layerIndex += 1;
                    loadedImage(material, imageTexture,
                        layerContent.calcHeight || layerContent.height,
                        layerContent.calcWidth || layerContent.width);
                };
                const onError = () => {
                    // NOTE: PNGs that have been converted to WEBP sometimes fail on Safari browsers. It may be connected to this
                    // issue: https://bugs.webkit.org/show_bug.cgi?id=219977
                    if (!fallbackAttempted) {
                        if (url.toString().indexOf("https://cdn.visualpedia.igg", 0) === -1) {
                            logError(
                                new Error(
                                    `Image could not be found: '${url}' (${layerContent.url}). Attempting to request fallback image.`
                                )
                            );
                        }
                        // TODO DESCR-218: revisit
                        // const originalUrl = createCdnOriginalFormatImageUrl(
                        //     layerContent,
                        //     qualityToUse.fraction * layerContent.width
                        // );
                        const originalUrl = getLayerContentUrl(layerContent);

                        loader.load(originalUrl, success, undefined, onError);
                        fallbackAttempted = true;
                    } else {
                        if (url.toString().indexOf("https://cdn.visualpedia.igg", 0) === -1) {
                            logError(
                                new Error(
                                    `Attempted fallback and image and it could still not be found: '${url}' (${layerContent.url}).`
                                )
                            );
                        }
                        rendererStore.loadedLayer(layerId, url);
                    }
                };
                //console.log("url is: "  + url)
                //console.log(getImage)
                //console.log(this.templateJson)
                //console.log(layerContent.width)

                const IMAGE_SIZE_LIMIT = 25e6;
                const getMaxWidth = (width: number, height: number, dpr: number) => {
                    const maxWidth = Math.sqrt((IMAGE_SIZE_LIMIT * width) / height);
                    return Math.floor(Math.min(maxWidth, width) / dpr);
                };

                const maxWidth = getMaxWidth(layerContent.width, layerContent.height, rendererStore.dpr);
                let targetWidth: number = maxWidth;

                if (layerContent.width > layerContent.height) {
                    if (layerContent.width > 2000) {
                        targetWidth = maxWidth / 2;
                    }
                } else if (layerContent.height > layerContent.width) {
                    if (layerContent.width < 2000 && layerContent.width > 1024) {
                        targetWidth = 1024;
                    }
                } else {
                    targetWidth = maxWidth / 2;
                }

                const params = new Map<string, string>();
                params.set("processing", `rs:fit:${targetWidth}`);
                const optimizedUrl = this.imageOptimizer(url, params);
                loader.load(optimizedUrl, success, undefined, onError);
                // cache the layer
                this.layerContentCache[layerId] = content;
            }
        }
    }

    renderLayer = (
        layer: LayerRender,
        parent: THREE.Object3D,
        timer: number,
        backwards: boolean,
        maxVector: TODOAny,
        isActive: boolean,
        order: number,
        responsiveScaleFactor: number
    ): Array<THREE.Object3D> => {
        const usedChildren: Array<THREE.Object3D> = [];
        let layerMesh = this.layerMeshes[layer.id] as THREE.Mesh;
        const isGroup = layer.type === LayerType.group;

        if (layer.type === LayerType.object3d || layer.type === LayerType.text || layer.type === LayerType.svg) {
            const obj = this.layerState3d[layer.id];
            if (!obj) {
                return usedChildren;
            }
        }

        if (isGroup) {
            if (!layerMesh || !this.groups[layer.id]) {
                layerMesh = new THREE.Group() as TODOAny;
                this.layerMeshes[layer.id] = layerMesh;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                this.groups[layer.id] = layerMesh as any;
                layerMesh.name = layer.name;
            }
        }

        if (layer.type === LayerType.object3dChild) {
            const childObject: THREE.Object3D = this.objectCache[layer.id];
            layerMesh = childObject as THREE.Mesh;
        }

        if (!layerMesh) {
            return usedChildren;
        }

        let pivotGroup = this.pivots[layer.id];

        // add the layerMesh to the scene, but only when it's not a group
        // special handling for object3dchild
        if (!pivotGroup && layer.type !== LayerType.object3dChild) {
            // first we create a pivot object
            pivotGroup = new THREE.Group();
            this.pivots[layer.id] = pivotGroup;
            pivotGroup.add(layerMesh);
            if (!this.props.isProduction) {
                const geometry = new THREE.SphereGeometry(100, 4, 2);
                const material = new THREE.MeshBasicMaterial({
                    color: 0xff0000,
                    wireframe: true,
                });
                const pivotCircle = new THREE.Mesh(geometry, material);
                pivotCircle.visible = false;
                this.pivotsCircles[layer.id] = pivotCircle;
                pivotGroup.add(pivotCircle);
            }
            parent.add(pivotGroup);
        }
        if (pivotGroup) {
            usedChildren.push(pivotGroup);
            if (pivotGroup.children[0] !== layerMesh) {
                pivotGroup.remove(pivotGroup.children[0]);
                const circle = pivotGroup.children[0]; // only exists in non-production (debug pivot circle)
                if (circle) pivotGroup.remove(circle);
                pivotGroup.add(layerMesh);
                if (!this.setVideoJit && circle) pivotGroup.add(circle);
            }
        }

        const isInLifetime =
            isGroup || layer.type === LayerType.object3dChild ? true : layer.from <= timer && layer.to >= timer;

        const checkForFrustum = (layerMesh: TODOAny, camera: THREE.PerspectiveCamera) => {
            let isInFrustum: boolean = false;
            const boundingBox: THREE.Box3 = new THREE.Box3().setFromObject(layerMesh);
            const frustum: THREE.Frustum = new THREE.Frustum();
            frustum.setFromProjectionMatrix(camera.projectionMatrix);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            frustum.planes.forEach((plane: any) => {
                plane.applyMatrix4(camera.matrixWorld);
            });
            if (frustum.intersectsBox(boundingBox)) {
                isInFrustum = true;
            }
            return isInFrustum;
        };

        let checkFrustum: boolean = false;
        let checkForOnScreen: boolean = true;

        if (layerMesh.userData.videoLiveUpdate) {
            if (this.camera) checkFrustum = checkForFrustum(layerMesh, this.camera);

            if (this.camera && this.webGLRenderer) {
                const vector: THREE.Vector3 = new THREE.Vector3();

                const widthHalf: number = 0.5 * this.webGLRenderer.getContext().canvas.width;
                const heightHalf: number = 0.5 * this.webGLRenderer.getContext().canvas.height;

                layerMesh.updateMatrixWorld();
                vector.setFromMatrixPosition(layerMesh.matrixWorld);
                vector.project(this.camera);

                vector.x = vector.x * widthHalf + widthHalf;
                vector.y = -(vector.y * heightHalf) + heightHalf;

                const boundingBox: THREE.Box3 = new THREE.Box3().setFromObject(layerMesh);

                if (
                    vector.y - boundingBox.max.y / 2 < 0 ||
                    vector.y - boundingBox.max.y / 2 > this.webGLRenderer.getContext().canvas.height
                ) {
                    checkForOnScreen = false;
                } else if (
                    vector.x - boundingBox.max.x / 2 < 0 ||
                    vector.x - boundingBox.max.x / 2 > this.webGLRenderer.getContext().canvas.width
                ) {
                    checkForOnScreen = false;
                }
            }

            const video = this.videoCache[layer.id];

            if (!checkForOnScreen) {
                video.pause();
                video.seek(0);
            }

            if (!checkFrustum) {
                video.pause();
                video.seek(0);
            } else {
                if (!video.isPlaying) {
                    video.isPlaying = true;
                    video.play();
                }
            }
        }

        if (layer.type === LayerType.video) {
            if (this.camera) checkFrustum = checkForFrustum(layerMesh, this.camera);

            if (this.camera && this.webGLRenderer) {
                const vector: THREE.Vector3 = new THREE.Vector3();

                const widthHalf: number = 0.5 * this.webGLRenderer.getContext().canvas.width;
                const heightHalf: number = 0.5 * this.webGLRenderer.getContext().canvas.height;

                layerMesh.updateMatrixWorld();
                vector.setFromMatrixPosition(layerMesh.matrixWorld);
                vector.project(this.camera);

                vector.x = vector.x * widthHalf + widthHalf;
                vector.y = -(vector.y * heightHalf) + heightHalf;

                const boundingBox: THREE.Box3 = new THREE.Box3().setFromObject(layerMesh);

                if (
                    vector.y - boundingBox.max.y / 2 < 0 ||
                    vector.y - boundingBox.max.y / 2 > this.webGLRenderer.getContext().canvas.height
                ) {
                    checkForOnScreen = false;
                } else if (
                    vector.x - boundingBox.max.x / 2 < 0 ||
                    vector.x - boundingBox.max.x / 2 > this.webGLRenderer.getContext().canvas.width
                ) {
                    checkForOnScreen = false;
                }
            }

            // Clamp layer.from at 1 to avoid trying to play videos that start at
            //  the first frame before user interaction (which is disallowed in
            // modern browsers).
            const isVideoInLifetime = isInLifetime && Math.max(1, layer.from) <= timer;

            const video = this.videoCache[layer.id];
            const content = layer.content as VideoContent;
            // Read playbackMode from latest JSON (templateJson), not from the immutable
            // PlayablePresentation tree — this allows live mode switching via editor buttons.
            const jsonLayer = this.templateJson?.data?.data?.layers?.find((l: any) => l?.id === layer.id);
            if (jsonLayer?.content?.playbackMode) {
                (content as any).playbackMode = jsonLayer.content.playbackMode;
            }
            if (isInLifetime) {
                if (content.transparent && video.videoMaterial) {
                    video.videoMaterial.update();
                } else if (!content.transparent && video.videoTexture) {
                    video.videoTexture.needsUpdate = true;
                }
            }

            const { videosThatShouldBePlaying, useVideoPlayWorkaround } = this.props.renderer;
            const playQueueNeeded = useVideoPlayWorkaround && !content.muted;

            if (!checkFrustum) {
                video.pause();
                video.seek(0);
            }

            if (!checkForOnScreen) {
                video.pause();
                video.seek(0);
            }

            const playbackMode = content.playbackMode || 'loop';

            if (playbackMode === 'scroll') {
                // Scroll-driven: video position follows scroll progress
                if (video.isPlaying) video.pause();
                if (isVideoInLifetime && video.videoPlayer) {
                    const duration = video.videoPlayer.duration || 1;
                    const layerDuration = Math.max(1, layer.to - layer.from);
                    const progress = Math.max(0, Math.min(1, (timer - layer.from) / layerDuration));
                    video.videoPlayer.currentTime = progress * duration;
                }
            } else if (playbackMode === 'pingpong') {
                // Ping-pong: manual currentTime control (forward then reverse)
                // We keep the video paused and drive currentTime ourselves,
                // because HTMLVideoElement does not support negative playbackRate.
                if (video.videoPlayer) {
                    video.videoPlayer.loop = false;
                }
                if (!isVideoInLifetime) {
                    if (video.isPlaying) video.pause();
                    video.seek(0);
                    video.resetPingPong();
                } else if (isVideoInLifetime) {
                    // Keep native playback paused — we drive currentTime manually
                    if (!video.videoPlayer.paused) video.videoPlayer.pause();
                    video.isPlaying = true; // mark as logically playing for texture updates
                    video.updatePingPong();
                }
            } else if (playbackMode === 'once') {
                // Play once then freeze on last frame
                if (video.videoPlayer) {
                    video.videoPlayer.loop = false;
                }
                if (!isVideoInLifetime) {
                    video.pause();
                    video.seek(0);
                    video.hasEnded = false;
                } else if (isVideoInLifetime) {
                    if (!video.isPlaying && !video.hasEnded) {
                        // Start playing only if we haven't finished yet
                        if (!playQueueNeeded) video.play();
                        else video.playVirtual();
                    }
                    // If the video has ended, keep it paused at the last frame
                    if (video.hasEnded && video.videoPlayer) {
                        video.pause();
                        video.videoPlayer.currentTime = Math.max(0, (video.videoPlayer.duration || 1) - 0.01);
                    }
                }
            } else {
                // Loop mode (default): standard play/pause behavior
                if (video.videoPlayer) {
                    video.videoPlayer.loop = true;
                }
                if (!isVideoInLifetime && video.isPlaying) {
                    video.pause();
                    video.seek(0);
                } else if (isVideoInLifetime && !video.isPlaying) {
                    if (!content.loop && backwards && content.dontRestartWhenGoingBack) {
                        video.seekToEnd();
                        video.playVirtual();
                    } else if (!playQueueNeeded) {
                        video.play();
                    } else {
                        video.playVirtual();
                    }
                }
            }

            if (playQueueNeeded) {
                if (video.isPlaying) {
                    videosThatShouldBePlaying.add(video);
                } else {
                    videosThatShouldBePlaying.delete(video);
                }
            }
        }

        if (layer.type === LayerType.lottie) {
            const lottiePlayer = this.lottieCache[layer.id];
            const lottieContent = layer.content as LottieContent;
            // Read playbackMode from latest JSON (templateJson), not from the immutable
            // PlayablePresentation tree — this allows live mode switching via editor buttons.
            const jsonLottieLayer = this.templateJson?.data?.data?.layers?.find((l: any) => l?.id === layer.id);
            if (jsonLottieLayer?.content?.playbackMode) {
                (lottieContent as any).playbackMode = jsonLottieLayer.content.playbackMode;
            }

            if (lottiePlayer?.texture) {
                if (isInLifetime) {
                    lottiePlayer.texture.needsUpdate = true;
                }

                const playbackMode = lottieContent.playbackMode || 'loop';

                if (playbackMode === 'scroll') {
                    // Scroll-driven: frame position follows scroll progress
                    if (lottiePlayer.isPlaying) lottiePlayer.pause();
                    if (isInLifetime) {
                        const layerDuration = Math.max(1, layer.to - layer.from);
                        const progress = Math.max(0, Math.min(1, (timer - layer.from) / layerDuration));
                        lottiePlayer.seekToProgress(progress);
                        lottiePlayer.texture.needsUpdate = true;
                    }
                } else if (playbackMode === 'pingpong') {
                    if (!isInLifetime && lottiePlayer.isPlaying) {
                        lottiePlayer.pause();
                        lottiePlayer.seek(0);
                    } else if (isInLifetime) {
                        if (!lottiePlayer.isPlaying) {
                            lottiePlayer.play();
                        }
                        // Reverse direction at boundaries
                        if (lottiePlayer.animation) {
                            const currentFrame = lottiePlayer.animation.currentFrame;
                            const totalFrames = lottiePlayer.totalFrames;
                            if (currentFrame >= totalFrames - 1 && lottiePlayer.animation.playDirection === 1) {
                                lottiePlayer.animation.setDirection(-1);
                            } else if (currentFrame <= 1 && lottiePlayer.animation.playDirection === -1) {
                                lottiePlayer.animation.setDirection(1);
                            }
                        }
                    }
                } else if (playbackMode === 'once') {
                    if (!isInLifetime && lottiePlayer.isPlaying) {
                        lottiePlayer.pause();
                        lottiePlayer.seek(0);
                    } else if (isInLifetime && !lottiePlayer.isPlaying) {
                        if (lottiePlayer.animation) {
                            const currentFrame = lottiePlayer.animation.currentFrame;
                            if (currentFrame < lottiePlayer.totalFrames - 1) {
                                lottiePlayer.animation.setLoop(false);
                                lottiePlayer.play();
                            }
                        }
                    }
                } else {
                    // Loop mode (default)
                    if (!isInLifetime && lottiePlayer.isPlaying) {
                        lottiePlayer.pause();
                        lottiePlayer.seek(0);
                    } else if (isInLifetime && !lottiePlayer.isPlaying) {
                        if (lottiePlayer.animation) {
                            lottiePlayer.animation.setLoop(true);
                        }
                        lottiePlayer.play();
                    }
                }

                // Apply speed multiplier
                if (lottieContent.speed && lottiePlayer.animation) {
                    lottiePlayer.animation.setSpeed(lottieContent.speed);
                }
            }
        }

        if (layer.type === LayerType.light) {
            const helperLight = this.lightHelpers[layer.id];
            const lightShadowHelper = this.lightShadowHelpers[layer.id];
            if (helperLight) {
                helperLight.update();
                lightShadowHelper.update();
            }
        }

        const isParentVisible = layerMesh?.parent?.visible ?? true;

        // TODO: use path
        const visible = (this.visibilityOverrides[layer?.id] ?? layer?.visible ?? true) && isInLifetime && isParentVisible;
        layerMesh.visible = visible;

        if (!visible) {
            const pivotCircle = this.pivotsCircles[layer.id];
            if (pivotCircle) {
                pivotCircle.visible = false;
            }
            return usedChildren;
        }

        // Only change attributes when visible
        const layer3D = layer.layer3D(timer);
        const layer3Dscale = layer3D.scale!;
        const layer3Dopacity = layer3D.opacity!;
        const layer3Drotation = layer3D.rotation!;
        const layer3Dposition = layer3D.position!;
        const useZeroScale = layer3Dscale[0] === 0 || layer3Dscale[1] === 0 || layer3Dscale[2] === 0;
        const opacityZero = layer3Dopacity === 0;

        // 3d objects make a lot of problems with the ordering, which are not entirely clear why they happen
        // it seems to work when we don't set something for the object3d
        layerMesh.renderOrder = layer.type !== LayerType.object3d ? order : 0;

        if (layer.type === LayerType.text && layerMesh.children[0]) {
            const mesh = layerMesh.children[0] as THREE.Mesh;
            mesh.renderOrder = order;
        }

        // TODO: combine this with general visibility
        if (useZeroScale || opacityZero) {
            layerMesh.visible = false;
            return usedChildren;
        }

        let material = layerMesh.material as THREE.Material;
        const parentOpacity = parent ? parent.userData.opacity || 1 : 1;
        const opacity = layer3Dopacity * parentOpacity;

        switch (layer.type) {
            case LayerType.group:
                // save the opacity in the userdata of the group
                layerMesh.userData.opacity = layer3Dopacity * parentOpacity;
                break;
            case LayerType.text: {
                if (layerMesh.children[0]) {
                    const mesh = layerMesh.children[0] as THREE.Mesh;
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    material = (mesh as any).material;
                    material.opacity = opacity;
                }
                break;
            }

            case LayerType.video:
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                if ((layer.content as VideoContent).transparent && (material as any).uniforms) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (material as any).uniforms.opacity.value = opacity;
                } else {
                    material.opacity = opacity;
                }
                break;
            case LayerType.lottie:
                material.opacity = opacity;
                break;
            case LayerType.image:
                // Background image layers (isBackground / is_background) render at
                // 20 % opacity so the photo is a subtle backdrop behind the chart.
                if ((layer as TODOAny).isBackground || (layer as TODOAny).is_background) {
                    material.opacity = 0.25;
                } else {
                    material.opacity = opacity;
                }
                break;
            case LayerType.svg:
                layerMesh.traverse((child) => {
                    if ((child as THREE.Mesh).isMesh) {
                        ((child as THREE.Mesh).material as THREE.Material).opacity = opacity;
                        child.renderOrder = layerMesh.renderOrder;
                    }
                });
                break;
            case LayerType.object3d:
                layerMesh.userData.opacity = opacity;
                if (layerMesh.isMesh) {
                    (layerMesh.material as THREE.Material).opacity = opacity;
                }
                break;
            case LayerType.object3dChild:
                if (layerMesh.isMesh) {
                    layerMesh.userData.opacity = opacity;
                    (layerMesh.material as THREE.Material).opacity = opacity;
                }
                break;
            default:
        }

        layerMesh.name = layer.name;

        const pivotPointIsZero = layer.pivotPoint[0] === 0 && layer.pivotPoint[1] === 0 && layer.pivotPoint[2] === 0;
        const doNotUsePivot =
            (layer.type === LayerType.object3d && pivotPointIsZero) || layer.type === LayerType.object3dChild;
        const usePivot = !doNotUsePivot;
        const zPosition = layer3Dposition[2];

        // layers with a custom z position should be rendered first and compared among themselves
        // object3ds are making again problems here and are ignored for now, as it still works with
        // some internal THREE.js bugs probably
        if (zPosition > 0 && layer.type !== LayerType.object3d && layer.type !== LayerType.object3dChild) {
            layerMesh.renderOrder = 999;
        }

        const newPosition = new THREE.Vector3(
            layer3Dposition[0] + layer.pivotPoint[0],
            layer3Dposition[1] + layer.pivotPoint[1],
            zPosition + layer.pivotPoint[2]
        ).multiply(maxVector);

        const relativePivotOffset = new THREE.Vector3(
            -layer.pivotPoint[0],
            -layer.pivotPoint[1],
            -layer.pivotPoint[2]
        ).multiply(maxVector);
        // make sure we don't scale with zero
        let scaleVector = new THREE.Vector3(...(useZeroScale ? MIN_SCALE : layer3Dscale));

        if (layer.type === LayerType.text) {
            const textContent: TextContent = layer.content;
            // the responsive scale factor will adjust font sizes on smaller screens
            const scaleFactor = 1 + ((textContent.responsiveScaling || 0) / 100) * (responsiveScaleFactor - 1);
            scaleVector = scaleVector.multiplyScalar(scaleFactor);
        }

        const toRad = [
            degreeToRad(layer3Drotation[0]),
            degreeToRad(layer3Drotation[1]),
            degreeToRad(layer3Drotation[2]),
        ] as Vector3Tuple;

        if (usePivot) {
            const pivotCircle = this.pivotsCircles[layer.id];
            if (pivotCircle) {
                pivotCircle.visible = isActive && layer.showPivotPoint;
                pivotCircle.position.setZ(zPosition);
            }
            pivotGroup.position.copy(newPosition);
            pivotGroup.scale.copy(scaleVector);
            pivotGroup.rotation.set(...toRad);
            layerMesh.position.copy(relativePivotOffset);
            // layerMesh.scale.set(1, 1, 1);
            layerMesh.rotation.set(0, 0, 0);
        } else {
            if (pivotGroup) {
                pivotGroup.position.set(0, 0, 0);
                pivotGroup.scale.set(1, 1, 1);
                pivotGroup.rotation.set(0, 0, 0);
                const pivotCircle = this.pivotsCircles[layer.id];
                if (pivotCircle) {
                    pivotCircle.visible = isActive && layer.showPivotPoint;
                }
            }
            if (layer.type === LayerType.object3dChild) {
                layerMesh.position.set(...layer3D.position!);
            } else {
                layerMesh.position.copy(newPosition);
            }
            layerMesh.scale.copy(scaleVector);
            layerMesh.rotation.set(...toRad);
        }

        return usedChildren;
    };

    /** Sentence-Bloom quote SVG must not appear on the cover (beforeStory scroll zone). */
    private hideSentenceBloomSvgForCover(layerId: string, timer: number, layerFrom: number): boolean {
        if (layerId !== 'svg-layer-1') return false;
        if (this.props.isCoverActive?.()) return true;
        if (timer <= 0) return true;
        if (timer < Math.max(1, layerFrom)) return true;
        return false;
    }

    renderLayerHTML = (
        layer: LayerRender,
        parent: CSS3DObject,
        timer: number,
        _: boolean,
        maxVector: TODOAny,
        isActive: boolean,
        order: number,
        responsiveScaleFactor: number
    ): Array<CSS3DObject> => {
        const usedChildren: Array<CSS3DObject> = [];
        let layerMesh = this.layerStateCSS3d[layer.id];
        // currently only used for the text, we need to access it for changing opacity and display
        const innerMesh = this.layerStateCSS3d[`${layer.id}-inner`];
        const isGroup = layer.type === LayerType.group;

        const isHtmlLayer = layer.type === LayerType.svg && layer.content.asHtml;
        const isMapLayer = layer.type === LayerType.map;

        if (!(isGroup || isHtmlLayer || isMapLayer)) {
            return usedChildren;
        }

        // Interactive map layers: drive camera + overlay transforms per frame
        if (isMapLayer) {
            this.renderMapLayer(layer, parent, timer);
            return usedChildren;
        }

        // SVG overlay layers: handle visibility/opacity/position directly on the DOM element
        if (isHtmlLayer) {
            const overlay = this.svgOverlays[layer.id];
            if (!overlay) return usedChildren;

            if (this.hideSentenceBloomSvgForCover(layer.id, timer, layer.from)) {
                overlay.style.display = 'none';
                return usedChildren;
            }

            const isInLifetime = layer.from <= timer && layer.to >= timer;
            const visible = (this.visibilityOverrides[layer?.id] ?? layer?.visible ?? true) && isInLifetime;

            if (!visible) {
                overlay.style.display = 'none';
                return usedChildren;
            }

            const layer3D = layer.layer3D(timer);
            const parentOpacity = parent ? parent.userData.opacity || 1 : 1;
            const effectiveOpacity = layer3D.opacity! * parentOpacity;

            // Accumulate scale from parent groups (multiplicative)
            const parentScale = parent?.userData.scale || [1, 1, 1];
            const scaleX = layer3D.scale![0] * parentScale[0];
            const scaleY = layer3D.scale![1] * parentScale[1];

            if (effectiveOpacity === 0 || scaleX === 0 || scaleY === 0) {
                overlay.style.display = 'none';
                return usedChildren;
            }

            // Accumulate position from parent groups (stored in userData by group rendering)
            const parentPos = parent?.userData.position || [0, 0];
            const posX = (layer3D.position![0] + parentPos[0]) * 100;
            const posY = (layer3D.position![1] + parentPos[1]) * -100; // Y inverted: WebGL up = CSS down

            // Accumulate rotation from parent groups (additive, Z-axis for 2D overlays)
            const parentRotation = parent?.userData.rotation || [0, 0, 0];
            const rotationZ = layer3D.rotation![2] + parentRotation[2];

            // Build combined CSS transform
            const transforms: string[] = [];
            if (posX !== 0 || posY !== 0) {
                transforms.push(`translate(${posX}%, ${posY}%)`);
            }
            if (scaleX !== 1 || scaleY !== 1) {
                transforms.push(`scale(${scaleX}, ${scaleY})`);
            }
            if (rotationZ !== 0) {
                transforms.push(`rotate(${rotationZ}deg)`);
            }

            overlay.style.display = 'block';
            overlay.style.opacity = `${effectiveOpacity}`;
            overlay.style.transform = transforms.join(' ');
            return usedChildren;
        }

        if (isGroup && (!layerMesh || !this.groupsCSS[layer.id])) {
            const element = document.createElement("div");
            layerMesh = new CSS3DObject(element);
            this.layerStateCSS3d[layer.id] = layerMesh;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            this.groupsCSS[layer.id] = layerMesh as any;
            layerMesh.name = layer.name;
        }

        if (!layerMesh) {
            return usedChildren;
        }

        let pivotGroup = this.pivotsCSS[layer.id];

        // add the layerMesh to the scene, but only when it's not a group
        // special handling for object3dchild
        if (!pivotGroup) {
            // first we create a pivot object
            const element = document.createElement("div");
            pivotGroup = new CSS3DObject(element);
            this.pivotsCSS[layer.id] = pivotGroup;
            pivotGroup.add(layerMesh);
            parent.add(pivotGroup);
        }
        if (pivotGroup) {
            usedChildren.push(pivotGroup);
        }

        const isInLifetime = isGroup || (layer.from <= timer && layer.to >= timer);

        const isParentVisible = layerMesh?.parent?.parent?.visible ?? true;

        // TODO: use path
        const visible = (this.visibilityOverrides[layer?.id] ?? layer?.visible ?? true) && isInLifetime && isParentVisible;
        // we need to apply opacity and display directly if we have an innermesh
        (innerMesh || layerMesh).visible = visible;
        (innerMesh || layerMesh).element.style.display = visible ? "block" : "none";

        if (!visible) {
            return usedChildren;
        }

        // Only change attributes when visible
        const layer3D = layer.layer3D(timer);
        const useZeroScale = layer3D.scale!.some((n) => n === 0);
        const opacityZero = layer3D.opacity === 0;

        (innerMesh || layerMesh).element.style.zIndex = order.toString();

        // TODO: combine this with general visibility
        if (useZeroScale || opacityZero) {
            // we need to apply opacity and display directly
            (innerMesh || layerMesh).visible = false;
            (innerMesh || layerMesh).element.style.display = "none";
            return usedChildren;
        }

        const parentOpacity = parent ? parent.userData.opacity || 1 : 1;
        const opacity = layer3D.opacity! * parentOpacity;

        switch (layer.type) {
            case LayerType.group: {
                // save the opacity, position, scale and rotation in the userdata of the group
                // so SVG overlay children can read them
                layerMesh.userData.opacity = layer3D.opacity! * parentOpacity;
                const parentPos = parent?.userData.position || [0, 0];
                layerMesh.userData.position = [
                    layer3D.position![0] + parentPos[0],
                    layer3D.position![1] + parentPos[1],
                ];
                // Accumulate scale (multiplicative) for SVG overlay children
                const parentScale = parent?.userData.scale || [1, 1, 1];
                layerMesh.userData.scale = [
                    layer3D.scale![0] * parentScale[0],
                    layer3D.scale![1] * parentScale[1],
                    layer3D.scale![2] * parentScale[2],
                ];
                // Accumulate rotation (additive) for SVG overlay children
                const parentRotation = parent?.userData.rotation || [0, 0, 0];
                layerMesh.userData.rotation = [
                    layer3D.rotation![0] + parentRotation[0],
                    layer3D.rotation![1] + parentRotation[1],
                    layer3D.rotation![2] + parentRotation[2],
                ];
                break;
            }
            case LayerType.svg:
                layerMesh.element.style.opacity = `${opacity}`;
                break;
            case LayerType.text:
                innerMesh.element.style.opacity = `${opacity}`;
                break;
            default:
        }

        layerMesh.name = layer.name;

        const usePivot = true;
        const zPosition = layer3D.position![2];

        // layers with a custom z position should be rendered first and compared among themselves
        if (zPosition > 0) {
            (innerMesh || layerMesh).element.style.zIndex = "100";
        }

        const newPosition = new THREE.Vector3(
            layer3D.position![0] + layer.pivotPoint[0],
            layer3D.position![1] + layer.pivotPoint[1],
            zPosition + layer.pivotPoint[2]
        ).multiply(maxVector);

        const relativePivotOffset = new THREE.Vector3(
            -layer.pivotPoint[0],
            -layer.pivotPoint[1],
            -layer.pivotPoint[2]
        ).multiply(maxVector);
        // make sure we don't scale with zero
        let scaleVector = new THREE.Vector3(...(useZeroScale ? MIN_SCALE : layer3D.scale!));

        const textContent = layer.content as TextContent;
        if (layer.type === LayerType.text) {
            const scaleFactor = 1 + ((textContent.responsiveScaling || 0) / 100) * (responsiveScaleFactor - 1);
            scaleVector = scaleVector.multiplyScalar(scaleFactor);
        }

        const toRad = layer3D.rotation!.map(degreeToRad) as Vector3Tuple;

        if (usePivot) {
            const pivotCircle = this.pivotsCircles[layer.id];
            if (pivotCircle) {
                pivotCircle.visible = isActive && layer.showPivotPoint;
                pivotCircle.position.setZ(zPosition);
            }
            pivotGroup.position.copy(newPosition);
            pivotGroup.scale.copy(scaleVector);
            pivotGroup.rotation.set(...toRad);
            layerMesh.position.copy(relativePivotOffset);
            // layerMesh.scale.set(1, 1, 1);
            layerMesh.rotation.set(0, 0, 0);
        } else {
            if (pivotGroup) {
                pivotGroup.position.set(0, 0, 0);
                pivotGroup.scale.set(1, 1, 1);
                pivotGroup.rotation.set(0, 0, 0);
                const pivotCircle = this.pivotsCircles[layer.id];
                if (pivotCircle) {
                    pivotCircle.visible = isActive && layer.showPivotPoint;
                }
            }
            layerMesh.position.copy(newPosition);
            layerMesh.scale.copy(scaleVector);
            layerMesh.rotation.set(...toRad);
        }

        return usedChildren;
    };

    updateLayer(layer: LayerRender, timer: number) {
        if (!WebGLAssets.allLayersSet) return;
        switch (layer.type) {
            case LayerType.object3d:
                this.updateObject(layer.id, layer.content, layer.setObject!);
                break;
            case LayerType.object3dChild:
                this.updateMaterial(layer.id, layer.content);
                break;
            case LayerType.light:
                this.updateLight(layer.id, layer.content);
                break;
            case LayerType.text:
                this.updateText(layer.id, layer.content);
                break;
            case LayerType.image:
                this.updateImage(layer.id, layer.content);
                break;
            case LayerType.video:
                this.updateVideo(layer.id, layer.content);
                break;
            case LayerType.lottie:
                this.updateLottie(layer.id, layer.content as LottieContent);
                break;
            case LayerType.svg:
                if (layer.content.asHtml) {
                    this.updateHTMLSvg(layer.id, layer.content as SvgContent, timer);
                } else {
                    this.updateSvg(layer.id, layer.content);
                }
                break;
            case LayerType.map:
                this.updateMap(layer.id, layer.content as MapContent);
                break;
            default:
        }
    }

    invalidateTree() {
        for (const child of this.groupRoot!.children) {
            this.groupRoot!.remove(child);
        }
        this.groups = {};
        this.pivots = {};
    }

    invalidateCSSTree() {
        // destroy complete scene with fire
        //this.cssRendererDom.removeChild(this.css3DRenderer.domElement);
        //this.css3DRenderer = new CSS3DRenderer();
        //this.css3DRenderer.setSize(_rendererStore.width, _rendererStore.height);
        //this.cssRendererDom.appendChild(this.css3DRenderer.domElement);
        const element = document.createElement("div");
        this.groupRootCSS = new CSS3DObject(element);
        // this.css3DScene = new THREE.Scene();
        // this.css3DScene.add(this.groupRootCSS);
        // do not trash the content cache
        // this.layerContentCache = {};
        this.layerStateCSS3d = {};
        this.pivotsCSS = {};
        this.groupsCSS = {};
        // Remove SVG overlay DOM elements
        for (const overlay of Object.values(this.svgOverlays)) {
            overlay.remove();
        }
        this.svgOverlays = {};

        // Remove interactive map instances and their DOM containers
        for (const [layerId, map] of Object.entries(this.mapInstances)) {
            try { map.remove?.(); } catch { /* swallow */ }
            // Clean up marker refs
            const markers = (this as any)[`${layerId}_markers`] as any[] | undefined;
            if (markers) markers.forEach((m: any) => m.remove?.());
            delete (this as any)[`${layerId}_markers`];
            delete (this as any)[`${layerId}_markers_hash`];
        }
        for (const container of Object.values(this.mapContainers)) {
            container.remove();
        }
        this.mapInstances = {};
        this.mapContainers = {};
    }

    // Main loop, where we animate everything
    animate(timer: number) {
        if (!this.webGLRenderer || !this.camera || !this.webGlScene || !this.ambientLight || !this.controls) {
            throw new Error("Called RendererWebGL#animate before calling init function.");
        }
        // console.log("test-animate", timer, this);

        const SHOW_HELPERS = !this.props.isProduction;
        const { backwards } = this.props.slidePlayer;
        const { webGlScene } = this;
        void this.css3DScene; // legacy: CSS3D scene reference kept for future use

        const rendererStore = this.props.renderer;

        const rendererPresentation = this.props.presentation;

        const { presentationOptions, slides, groupTree } = rendererPresentation;

        const safeAreas = slides.map((slide) => {
            const options = slide.options || presentationOptions.slideOptions;
            return {
                from: slide.from,
                x: options.safeAreaXPosition,
                y: options.safeAreaYPosition,
            };
        });
        this.ambientLight!.intensity = presentationOptions.ambientIntensity;

        if (this.webGLRenderer.getPixelRatio() !== rendererStore.dpr) {
            this.webGLRenderer.setPixelRatio(rendererStore.dpr);
        }

        // update renderer stuff
        if (SHOW_HELPERS) {
            if (!rendererStore.useOrbitControls) {
                this.camera.lookAt(new THREE.Vector3(...rendererStore.cameraLookAt!));
                this.camera.position.copy(new THREE.Vector3(...rendererStore.cameraPosition!));
            }
            this.camera.fov = rendererStore.fov!;
            this.camera.far = rendererStore.far!;
            this.camera.updateProjectionMatrix();
        }

        this.controls.enabled = rendererStore.useOrbitControls;

        // update height/width stuff
        const size = new THREE.Vector2();
        this.webGLRenderer.getSize(size);
        const sizeChanged = size.width !== rendererStore.width || size.height !== rendererStore.height;

        if (sizeChanged) {
            this.webGLRenderer.setSize(rendererStore.width, rendererStore.height, false);
            //this.css3DRenderer.setSize(rendererStore.width, rendererStore.height);
            this.camera.updateProjectionMatrix();
        }

        const { SLIDE_WIDTH, SLIDE_HEIGHT, SLIDE_DEPTH } = RendererStore;
        const { bounds, cutOffFillerX, cutOffFillerY, closestPossibleAspect, useMaxWidth } =
            rendererStore.calculateBounds();

        const boundingBox = {
            min: new THREE.Vector3(SLIDE_WIDTH / -2, SLIDE_HEIGHT / -2, SLIDE_DEPTH / -2),
            max: new THREE.Vector3(SLIDE_WIDTH / 2, SLIDE_HEIGHT / 2, SLIDE_DEPTH / 2),
        };

        this.webGLRenderer.setViewport(bounds.newLeft, bounds.newTop, bounds.newWidth, bounds.newHeight);
        this.webGLRenderer.setScissor(bounds.newLeft, bounds.newTop, bounds.newWidth, bounds.newHeight);

        this.camera.aspect = closestPossibleAspect;
        this.camera.updateProjectionMatrix();

        // visibleHeight and visibleWidth is the camera calculation to see what our ranges actually are
        const { height: visibleHeight, width: visibleWidth } = rendererStore.useOrbitControls
            ? {
                  width: rendererStore.width,
                  height: rendererStore.width * this.camera.aspect,
              }
            : RendererStore.visibleSize(this.camera.fov, this.camera.position.z, this.camera.aspect);

        // how much to we need to scale down according to slide-width and available width?
        const scaleFactor = useMaxWidth ? visibleWidth / SLIDE_WIDTH : visibleHeight / SLIDE_HEIGHT;

        // Scaling values available for debugging if needed:
        // containerW, containerH, cameraAspect, scaleFactor, useMaxWidth, bounds

        // we start to scale the font when we get to small screens
        const responsiveScaleFactor = Math.max(900 / Math.min(rendererStore.width, rendererStore.height), 1);

        if (!this.groupRoot) {
            this.groupRoot = new THREE.Group();
            const element = document.createElement("div");
            this.groupRootCSS = new CSS3DObject(element);
            this.webGlScene.add(this.groupRoot);
            //this.css3DScene!.add(this.groupRootCSS);

            // set up a mesh used as the boundary
            if (SHOW_HELPERS) {
                this.uiRoot = new THREE.Group();
                this.webGlScene.add(this.uiRoot);

                const safeSquareGeometry = new THREE.PlaneGeometry(1, 1, 1);
                const safeSquareMaterial = new THREE.MeshBasicMaterial({
                    color: 0x00ff00,
                    opacity: 0.3,
                    transparent: true,
                    wireframe: true,
                });
                this.safeSquare = new THREE.Mesh(safeSquareGeometry, safeSquareMaterial);
                this.uiRoot.add(this.safeSquare);
            }
        }
        if (this.safeSquare) {
            // be above everything!
            this.safeSquare.renderOrder = 9999;
            const material = this.safeSquare.material as THREE.MeshBasicMaterial;
            // TODO DESCR-218: revisit
            // material.opacity = this.props.editorStore!.showSafeArea ? 1 : 0;
            material.opacity = 0;
        }

        this.groupRoot!.scale.set(scaleFactor, scaleFactor, scaleFactor);
        this.groupRootCSS!.scale.set(scaleFactor, scaleFactor, scaleFactor);

        // TODO: this shouldn't be calculated on every frame
        const slideToIndex = safeAreas.findIndex((s) => s.from >= timer);
        const nextSlideSafeArea = slideToIndex !== -1 ? safeAreas[slideToIndex] : safeAreas[safeAreas.length - 1];
        const currentSlideSafeArea = slideToIndex > 0 ? safeAreas[slideToIndex - 1] : nextSlideSafeArea;
        const duration = nextSlideSafeArea.from - currentSlideSafeArea.from;
        const progress = slideToIndex === 0 ? 0 : (timer - currentSlideSafeArea.from) / duration;

        const safeXAlignment = interpolateLinearDirectly(
            currentSlideSafeArea.x,
            nextSlideSafeArea.x,
            Easings.quadInOut(progress)
        );
        const safeYAlignment = interpolateLinearDirectly(
            currentSlideSafeArea.y,
            nextSlideSafeArea.y,
            Easings.quadInOut(progress)
        );

        const safeXPosition = safeXAlignment / -100; // flipped sign
        const safeYPosition = safeYAlignment / 100;
        const cutOffFillerXOffset = (cutOffFillerX * safeXPosition * SLIDE_WIDTH * scaleFactor) / 2;
        const cutOffFillerYOffset = (cutOffFillerY * safeYPosition * SLIDE_HEIGHT * scaleFactor) / 2;

        this.groupRoot.position.set(cutOffFillerXOffset, cutOffFillerYOffset, 0);
        this.groupRootCSS!.position.set(cutOffFillerXOffset, cutOffFillerYOffset, 0);

        if (SHOW_HELPERS) {
            this.safeSquare!.scale.set(SLIDE_WIDTH / 2, SLIDE_HEIGHT / 2, 2);
            this.safeSquare!.position.set(-(safeXPosition / 2) * SLIDE_WIDTH, -(safeYPosition / 2) * SLIDE_HEIGHT, 1);
        }

        if (SHOW_HELPERS) {
            this.uiRoot!.scale.copy(this.groupRoot.scale);
            this.uiRoot!.position.copy(this.groupRoot.position);
        }

        // TODO DESCR-218: revisit
        // const currentlySelectedLayer = this.props.isProduction ? null : this.props.editorStore!.currentLayer;
        const currentlySelectedLayer = null;

        const usedChildren: Record<string, THREE.Object3D[]> = {};

        let numLayers = 0;
        crawl(
            groupTree,
            () => {
                numLayers += 1;
            },
            { getChildren: (l: TODOAny) => l?.layers }
        );

        let layerIndex = 0;
        WebGLAssets.layerIndex = 0;
        crawl(
            groupTree,
            (layer: LayerRender, context: TODOAny) => {
                this.updateLayer(layer, timer);
                const { parent } = context;
                const layerParent = parent ? this.layerMeshes[parent.id] : this.groupRoot!;
                const parentId = parent ? parent.id : "stage";
                const active = currentlySelectedLayer === layer;
                usedChildren[parentId] = usedChildren[parentId] || [];
                const order = numLayers - layerIndex;

                const newUsedChildren = this.renderLayer(
                    layer,
                    layerParent,
                    timer,
                    backwards,
                    boundingBox.max,
                    active,
                    order,
                    responsiveScaleFactor
                );
                usedChildren[parentId].push(...newUsedChildren);
                layerIndex += 1;
            },
            { getChildren: (l: TODOAny) => l?.layers }
        );

        const usedCSSChildren: Record<string, CSS3DObject[]> = {};
        layerIndex = 0;
        crawl(
            groupTree,
            (layer: LayerRender, context: TODOAny) => {
                const { parent } = context;
                const layerParent = parent ? this.layerStateCSS3d[parent.id] : this.groupRootCSS!;
                const parentId = parent ? parent.id : "stage";
                const active = currentlySelectedLayer === layer;
                usedCSSChildren[parentId] = usedCSSChildren[parentId] || [];
                const order = numLayers - layerIndex;

                const newUsedChildren = this.renderLayerHTML(
                    layer,
                    layerParent,
                    timer,
                    backwards,
                    boundingBox.max,
                    active,
                    order,
                    responsiveScaleFactor
                );
                usedCSSChildren[parentId].push(...newUsedChildren);
                layerIndex += 1;
            },
            { getChildren: (l: TODOAny) => l?.layers }
        );

        // after rendering and setting the meshes
        // we check if we need to rebuild the tree
        // this only needs to be done when editing
        let invalidateTree = false;
        if (!this.props.isProduction) {
            crawl(
                groupTree,
                (layer: LayerRender, context: TODOAny) => {
                    const layerMesh = this.layerMeshes[layer.id];

                    // deleting is not as clean when the layer is not discarded, so we have to clean up somewhere
                    if (LayerType.svg === layer.type) {
                        if (layerMesh && layer.content.asHtml) {
                            delete this.layerMeshes[layer.id];
                            delete this.layerState3d[layer.id];
                            invalidateTree = true;
                        }
                    }
                    if (layer.type === LayerType.group) {
                        const children = usedChildren[layer.id];
                        if (!children) {
                            return;
                        }

                        // Delete unused groups from parent
                        // We don't need to do this in production as the layers won't change
                        for (const child of layerMesh.children) {
                            // when we currently change the scene tree we start over and completely create the scene
                            if (!children.find((c: TODOAny) => c.uuid === child.uuid)) {
                                invalidateTree = true;
                                context.break();
                                break;
                            }
                        }
                    }
                },
                { getChildren: (l: TODOAny) => l?.layers }
            );

            if (invalidateTree) {
                this.invalidateTree();
            }

            let invalidateCSSTree = false;
            crawl(
                groupTree,
                (layer: LayerRender, context: TODOAny) => {
                    const layerMesh = this.layerStateCSS3d[layer.id];
                    // deleting is not as clean when the layer is not discarded, so we have to clean up somewhere
                    if (LayerType.svg === layer.type) {
                        if (layerMesh && !layer.content.asHtml) {
                            invalidateCSSTree = true;
                            context.break();
                            return;
                        }
                    }
                    if (layer.type === LayerType.group) {
                        const children = usedCSSChildren[layer.id];
                        if (!children) {
                            return;
                        }

                        // Delete unused groups from parent
                        // We don't need to do this in production as the layers won't change
                        for (const child of layerMesh.children) {
                            // when we currently change the scene tree we start over and completely create the scene
                            if (!children.find((c: TODOAny) => c.uuid === child.uuid)) {
                                invalidateCSSTree = true;
                                context.break();
                                return;
                            }
                        }
                    }
                },
                { getChildren: (l: TODOAny) => l?.layers }
            );

            if (invalidateCSSTree) {
                this.invalidateCSSTree();
            }

            if (invalidateTree || invalidateCSSTree) {
                this.animate(timer);
                return;
            }
        }

        rendererStore.collectStats();

        this.webGLRenderer.render(webGlScene, this.camera);
        //this.css3DRenderer.render(css3DScene!, this.camera);
    }

    start = () => {
        this.props.renderer.resetLayers();
        const launchPreso = () => {
            this.init();
            this.animationTimeOut = setTimeout(() => {
                this.props.start(this.animate);
            }, 0);

            const layerIds: string[] = [];
            crawl(
                this.props.presentation.groupTree,
                (layer: LayerRender) => {
                    if (
                        ([
                            LayerType.image,
                            LayerType.text,
                            LayerType.object3d,
                            LayerType.object3dChild,
                            LayerType.video,
                            LayerType.lottie,
                            LayerType.light,
                            LayerType.svg,
                            LayerType.map,
                        ] as string[]).includes(layer.type)
                    ) {
                        layerIds.push(layer.id);
                    } else if (!([LayerType.group] as string[]).includes(layer.type)) {
                        logError(new Error(`Layer with unknown type: ${layer.type}`));
                    }
                },
                { getChildren: (l: TODOAny) => l?.layers }
            );
            this.props.renderer.setLayers(layerIds);
            WebGLAssets.allLayersSet = true;
        };

        const startPresentation = () => {
            launchPreso();
        };

        if (!this.props.isProduction) {
            startPresentation();
        } else {
            this.props.renderer.setStartFunction(startPresentation);
        }
    };

    stop = () => {
        if (this.animationTimeOut) {
            clearTimeout(this.animationTimeOut);
        }
        if (this.interval) {
            clearInterval(this.interval);
        }
        // Remove document-level event listener to prevent leaks across re-inits
        if (this._visibilityHandler) {
            document.removeEventListener("visibilitychange", this._visibilityHandler);
            this._visibilityHandler = undefined;
        }
        this.props.stop();
        for (const video of Object.values(this.videoCache)) {
            video.pause();
            video.delete();
        }
        this.videoCache = {};
        for (const lottiePlayer of Object.values(this.lottieCache)) {
            lottiePlayer.pause();
            lottiePlayer.delete();
        }
        this.lottieCache = {};
        try {
            if (this.webGLRenderer) {
                this.webGLRenderer.forceContextLoss();
            }
        } catch (error) {
            logError(error as Error);
        }
    };

    dispose = () => {
        // Dispose OrbitControls (removes its internal event listeners)
        this.controls?.dispose();

        // Dispose CSS3DRenderer DOM element
        this.css3DRenderer?.domElement?.remove();

        // Remove SVG overlay DOM elements
        for (const overlay of Object.values(this.svgOverlays)) {
            overlay.remove();
        }
        this.svgOverlays = {};

        // Safety cleanup: remove global THREE reference if it exists
        delete (window as any).THREE;

        // Traverse the entire scene graph and dispose all GPU resources:
        // geometries, materials (including all texture maps), and troika text meshes.
        // Without this, each editor session leaks textures / geometries on the GPU.
        if (this.webGlScene) {
            this.webGlScene.traverse((object: THREE.Object3D) => {
                // Dispose troika-three-text meshes (they allocate SDF textures)
                if ((object as any).dispose && (object as any).text !== undefined) {
                    (object as any).dispose();
                }

                if (object instanceof THREE.Mesh) {
                    object.geometry?.dispose();

                    const materials = Array.isArray(object.material)
                        ? object.material
                        : [object.material];

                    for (const material of materials) {
                        if (!material) continue;
                        // Dispose all texture slots
                        const mat = material as any;
                        if (mat.map) mat.map.dispose();
                        if (mat.lightMap) mat.lightMap.dispose();
                        if (mat.bumpMap) mat.bumpMap.dispose();
                        if (mat.normalMap) mat.normalMap.dispose();
                        if (mat.specularMap) mat.specularMap.dispose();
                        if (mat.envMap) mat.envMap.dispose();
                        if (mat.alphaMap) mat.alphaMap.dispose();
                        if (mat.aoMap) mat.aoMap.dispose();
                        if (mat.displacementMap) mat.displacementMap.dispose();
                        if (mat.emissiveMap) mat.emissiveMap.dispose();
                        if (mat.metalnessMap) mat.metalnessMap.dispose();
                        if (mat.roughnessMap) mat.roughnessMap.dispose();
                        material.dispose();
                    }
                }
            });
            this.webGlScene.clear();
        }

        // Clear object caches so GC can collect them
        this.groups = {};
        this.groupsCSS = {};
        this.layerMeshes = {};
        this.slideLayerMeshes = {};
        this.layerContentCache = {};
        this.objectCache = {};
        this.layer3DCache = {};
        this.layerState3d = {};
        this.layerStateCSS3d = {};
        this.lightState = {};
        this.lightHelpers = {};
        this.lightShadowHelpers = {};
        this.pivots = {};
        this.pivotsCSS = {};
        this.pivotsCircles = {};

        if (this.webGLRenderer) {
            this.webGLRenderer.dispose(); // frees GPU memory
            try {
                const gl = this.webGLRenderer.getContext();
                const extension = gl.getExtension("WEBGL_lose_context");
                if (extension) {
                    // force WebGL context release — critical for Safari
                    extension.loseContext();
                }
            } catch {
                // Context may already be lost
            }
        }
    };
}
